import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Select, { SingleValue, components } from "react-select";
import { Addresses, PaymentInfoItem, PaymentMethod, PaymentGateway, Warehouse, Zone, Rack, Bin } from "types";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { RemovalStatusBadge } from "components/Removalstatus";
import { StatusBadge } from "components/Productlocationstatus";
import PaymentMethods from "components/PaymentMethods";
import InstallationStatusTracker from "components/InstallationStatusTracker";
import SectionHeader from "components/SectionPillPropsIcons";
import OrderInvoice from "components/OrderInvoice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import OrderLog, { buildRemovalLogEventsFromOrder } from "../../components/OrderLog";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import PaymentPanel from "components/PaymentOptionsCard";
import Instructions from "components/Instructions";
import AddressFormModal from "components/AddAddressFormModal";
import ProofSection, { ProofFile } from "../../components/ProofOfInstallation";
import type { InvoiceType } from "components/InvoiceDocument";
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import PONumber from "components/PONumber";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
    applyRemovalWorkOrderResolveResponse,
    buildRemovalResolvePayload,
    getOrderDiscountFieldDisplay,
    mapInstallationSpecialFeeForResolve,
    mapInstallationSpecialFeeForSubmit,
    mapSavedInstallationSpecialFeeToUi,
    normalizeInstallationSelectedFee,
    overlayRemovalSubmitFromResolved,
    postRemovalOrderResolve,
    type InstallationResolveAddress,
    type OrderResolveAction,
    type ResolvedRemovalOrder,
    patchRemovalOrderTaxComplete,
} from "orderResolve";
import {
    requestInstallationTaxRefund,
} from "components/Tax/INstallationandRemovalsTaxEngine";
import { Addresstax } from "types";
import {
    PostVersionOrdernumber,
    GetVersionOrdernumber,
} from "components/Tax/InstallationandRemovalTaxcloud";
import { getAuthCookie } from "../../utils/authCookies";
import { sendRemovalOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
// Active tax type
import { getActiveTaxType } from "components/Tax/manualtax";

const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    // border: "1px solid #dee2e6",          // Light border
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

/** Show a readable label for generated custom-product SKUs. */
function displayRemovalSku(item: { sku?: string; custom_sku?: string; inventory_source?: string }): string {
    if (String(item.inventory_source) !== "custom") return String(item.sku ?? "");

    const customSku = String(item.custom_sku ?? item.sku ?? "").trim();
    if (!customSku) return "";

    if (/^custom_sku(?:_|$)/i.test(customSku)) {
        return "Custom SKU";
    }

    return customSku;
}

function getRemovalLineItemId(item: { _id?: unknown; line_item_id?: string } | null | undefined): string {
    if (!item) return "";
    const fromStored = String(item.line_item_id ?? "").trim();
    if (fromStored) return fromStored;
    const raw = item._id;
    if (raw == null || raw === "") return "";
    if (typeof raw === "object") {
        const oid = (raw as { $oid?: string }).$oid;
        if (oid) return String(oid).trim();
        const str = (raw as { toString?: () => string }).toString?.();
        return str && str !== "[object Object]" ? String(str).trim() : "";
    }
    return String(raw).trim();
}

function normalizeRemovalLineItemFromApi<T extends Record<string, unknown>>(item: T): T & { line_item_id?: string } {
    const lineItemId = getRemovalLineItemId(item as { _id?: unknown; line_item_id?: string });
    return {
        ...item,
        ...(lineItemId ? { line_item_id: lineItemId } : {}),
    };
}

function withRemovalLineItemIdForPayload<T extends Record<string, unknown>>(item: T): Omit<T, "_id"> & { _id?: string } {
    const lineItemId = getRemovalLineItemId(item as { _id?: unknown; line_item_id?: string });
    const { _id: _omit, ...rest } = item as T & { _id?: string };
    if (!lineItemId) return rest;
    return { ...rest, _id: lineItemId };
}

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

type Combo = {
    warehouse_id: string;
    warehouse_name: string;
    zone_id: string;
    zone_name: string;
    rack_id: string;
    rack_name: string;
    bin_id: string;
    bin_name: string;
};

const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
        </components.DropdownIndicator>
    );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
    /* hide all indicators when disabled */
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),

    /* keep bg and cursor right; do NOT fade */
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),

    /* ——— make text BLACK when disabled ——— */
    singleValue: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    input: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
        WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        /* if field has no value, keep placeholder readable too */
        color: state.isDisabled ? "#000" : provided.color,
    }),

    /* if you ever use isMulti, keep chips readable */
    multiValue: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
    }),
    multiValueLabel: (p: any, s: any) => ({
        ...p,
        color: s.isDisabled ? "#000" : p.color,
    }),
    multiValueRemove: (p: any, s: any) => ({
        ...p,
        display: s.isDisabled ? "none" : p.display,
    }),
};
type InstallationTaxEngineHandle = {
    recalculate: (forceType?: "manual" | "taxcloud", options?: { allowRefund?: boolean }) => Promise<boolean>;
};

const AgentViewRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const location = useLocation();
    const type = location.state?.type ?? "View"; // 
    const mode = (type || "View").toLowerCase() as "view" | "edit";
    const isEdit = mode === "edit";
    const isViewMode = !isEdit;
    const Viewmode = !isEdit;
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const licenseType = getAuthCookie("licenseType");

    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string | null>(null);
    const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
    const [ordernumber, setOrderNumber] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [updatedProducts, setUpdatedProducts] = useState<any[]>([]);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
    const [payment, setPayment] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{ name: string; _id: string } | null>(null);
    const [clientSecret, setClientSecret] = useState<string | "">();
    const [instructions, setInstructions] = useState<string>("");
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<ProofFile[]>([]);
    const [updatedRotationalUnits, setUpdatedRotationalUnits] = useState<Record<string, {
        current_status: string;
        restock_quantity: number;
        writeoff_quantity: number;
        damaged_quantity: number;
        reason_to_writeoff: string;
        repair: number;
        bin_location: string;
        bin_location_display: string;
        note: string;
    }>>({});

    // ✅ State for special fees (bypasses Formik timing issues)
    const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
    const [isRecomputing, setIsRecomputing] = useState(false);
    const [removalCost, setRemovalCost] = useState<number | null>(null);
    const [zeroOrNegativeMsg, setZeroOrNegativeMsg] = React.useState<string>("");
    const [dynamicCostId, setDynamicCostId] = React.useState<string>("");
    const [dynamicTaxable, setDynamicTaxable] = React.useState<boolean>(false);
    const [showTaxDetails, setShowTaxDetails] = useState(false);
    // Tax type state
    const [taxType, setTaxType] = useState<"manual" | "taxcloud" | "unknown">("unknown");
    const [taxCloudFinalized, setTaxCloudFinalized] = useState(false);
    const taxFinalizeInProgressRef = React.useRef(false);
    const finalizeTaxAfterPaidPaymentRef = React.useRef<
        (options?: { force?: boolean }) => Promise<boolean>
    >(async () => false);
    const lastResolvedRemovalRef = useRef<ResolvedRemovalOrder | null>(null);
    const recomputeRef = useRef<((override?: {
        fees?: any[];
        discountCode?: string;
        action?: OrderResolveAction;
    }) => Promise<boolean | undefined>) | null>(null);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [racks, setRacks] = useState<Rack[]>([]);
    const [bins, setBins] = useState<Bin[]>([]);
    const [finalSubmittedItems, setFinalSubmittedItems] = useState<any>(null);
    const [productNotes, setProductNotes] = useState<Record<string, { bin_location: string; bin_location_display: string; note: string }>>({});
    const installationTaxRef = React.useRef<InstallationTaxEngineHandle | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);
    const invoiceRef = useRef<HTMLDivElement>(null);
    // ✅ Ref to track if special fees have been seeded from backend
    const seededSpecialFeesRef = useRef(false);

    // Fetch parent hierarchy to get SIGNAGE_FIRM
    const fetchParentHierarchy = async () => {
        if (!token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();

                // Find the parent with license_type "SIGNAGE_FIRM"
                const signageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "SIGNAGE_FIRM"
                );

                if (signageFirm) {
                    setParentSignageId(signageFirm.id || signageFirm._id);
                    setParentSignageName(signageFirm.name);
                }
            }
        } catch (error) {
            await handleApiError(error, {
                module: "removal",
                action: "Agent_View_Removal_Request_fetch_parent_hierarchy",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                method: "GET",
            }, token);
        }
    };

    useEffect(() => {
        fetchParentHierarchy();
    }, []);

    // Fetch final submitted items
    const fetchFinalSubmittedItems = async (removalOrderId: string) => {
        if (!removalOrderId) {
            safeConsole.log("No removal order ID available yet");
            return;
        }
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal-orders/${removalOrderId}/final-submitted-items`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                safeConsole.error("Failed to fetch final submitted items:", data.error);
                return;
            }

            setFinalSubmittedItems(data);
            safeConsole.log("Fetched final submitted items:", data);
        } catch (err) {
            await handleApiError(err, {
                module: "removal",
                action: "Agent_View_Removal_Request_fetch_final_submitted_items",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal-orders/${removalOrderId}/final-submitted-items`,
                method: "GET",
                additionalInfo: { removalOrderId },
            }, token);
        }
    };

    // Function to populate UI fields with final submitted items data
    const populateFieldsFromFinalSubmittedItems = () => {
        if (!finalSubmittedItems || !updatedProducts) return;

        // Group final submitted items by product_id
        const itemsByProduct = finalSubmittedItems.reduce((acc: any, item: any) => {
            if (!acc[item.product_id]) {
                acc[item.product_id] = [];
            }
            acc[item.product_id].push(item);
            return acc;
        }, {});

        // Update products with final submitted data
        const updatedProductsWithFinalData = updatedProducts.map((product: any) => {
            const finalItems = itemsByProduct[product.product_id] || [];

            if (product.inventory_source === "rotational") {
                // For rotational products, aggregate the data
                const aggregatedData = finalItems.reduce((acc: any, item: any) => {
                    acc.restocked += item.restocked || 0;
                    acc.damaged += item.damaged || 0;
                    acc.lost += item.lost || 0;
                    acc.repair += item.repair || 0;
                    return acc;
                }, { restocked: 0, damaged: 0, lost: 0, repair: 0 });

                return {
                    ...product,
                    restock_quantity: aggregatedData.restocked,
                    damaged_quantity: aggregatedData.damaged,
                    lost_quantity: aggregatedData.lost,
                    repair_quantity: aggregatedData.repair,
                };
            } else {
                // For normal products, aggregate the data
                const aggregatedData = finalItems.reduce((acc: any, item: any) => {
                    acc.restocked += item.restocked || 0;
                    acc.damaged += item.damaged || 0;
                    acc.lost += item.lost || 0;
                    return acc;
                }, { restocked: 0, damaged: 0, lost: 0 });

                return {
                    ...product,
                    restock_quantity: aggregatedData.restocked,
                    damaged_quantity: aggregatedData.damaged,
                    writeoff_quantity: aggregatedData.lost,
                };
            }
        });

        setUpdatedProducts(updatedProductsWithFinalData);

        // Update rotational units with final submitted data
        const updatedRotationalUnitsWithFinalData = { ...updatedRotationalUnits };
        const updatedProductNotesWithFinalData = { ...productNotes };

        finalSubmittedItems.forEach((item: any) => {
            if (item.inventory_source === "rotational" && item.serial_id) {
                // Create bin location string from location data
                const binLocationString = item.location ?
                    `${item.location.warehouse_name} > ${item.location.zone_name} > ${item.location.rack_name} > ${item.location.bin_name}` :
                    "";

                updatedRotationalUnitsWithFinalData[item.serial_id] = {
                    ...updatedRotationalUnitsWithFinalData[item.serial_id],
                    restock_quantity: item.restocked || 0,
                    damaged_quantity: item.damaged || 0,
                    writeoff_quantity: item.lost || 0,
                    repair: item.repair || 0,
                    current_status: item.condition_status || "Good",
                    bin_location: item.location?.bin_id || "",
                    bin_location_display: binLocationString,
                    note: item.submitted_notes || "",
                };
            } else if (item.inventory_source === "agent" || item.inventory_source === "office") {
                // Handle normal products - update product notes with bin location
                const binLocationString = item.location ?
                    `${item.location.warehouse_name} > ${item.location.zone_name} > ${item.location.rack_name} > ${item.location.bin_name}` :
                    "";

                updatedProductNotesWithFinalData[item.product_id] = {
                    ...updatedProductNotesWithFinalData[item.product_id],
                    bin_location: item.location?.bin_id || "",
                    bin_location_display: binLocationString,
                    note: item.submitted_notes || "",
                };
            }
        });

        setUpdatedRotationalUnits(updatedRotationalUnitsWithFinalData);
        setProductNotes(updatedProductNotesWithFinalData);
    };

    const normalizeRemovalToSalesOrder = (rem: any) => {
        if (!rem) return null;
        const billing = rem?.billing_address || null;
        const shipping = rem?.removal_details?.removal_address || null;
        const items = Array.isArray(rem?.line_items)
            ? rem.line_items.map((li: any) => ({
                product_name: li.product_name,
                sku: li.sku,
                quantity: li.quantity_to_be_removed ?? li.quantity ?? 0,
                unit_retail_price: li.unit_price,
                total_price: li.total_price,
                customizations: li.customizations || null,
            }))
            : [];
        return {
            order_number: rem?.order_number,
            order_status: rem?.status,
            payment_status: rem?.payment_status,
            customer_first_name: rem?.user_name || "",
            customer_last_name: "",
            customer_email: rem?.user_email,
            customer_phone: rem?.user_phone,
            created_at: rem?.created_at,
            billing_address: billing,
            shipping_address: shipping,
            items,
            payment_method: rem?.payment_method || "",
            total_amount: rem?.total_amount ?? rem?.removal_cost ?? 0,
            shipping_amount: 0,
            tax_amount: rem?.tax_amount ?? 0,
            discount_amount: rem?.discount_amount ?? 0,
        } as any;
    };


    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    // Build all possible bin combinations
    const combinations: Combo[] = useMemo(() => {
        if (!warehouses?.length || !zones?.length || !racks?.length || !bins?.length) return [];

        const warehouseMap = Object.fromEntries(warehouses?.map(w => [w._id, w]));
        const zoneMap = Object.fromEntries(zones?.map(z => [z._id, z]));
        const rackMap = Object.fromEntries(racks?.map(r => [r._id, r]));

        return bins
            ?.map(bin => {
                const rack = rackMap[bin.rack_id];
                const zone = zoneMap[bin.zone_id];
                const warehouse = warehouseMap[bin.warehouse_id];
                if (!rack || !zone || !warehouse) return null;

                return {
                    warehouse_id: warehouse._id,
                    warehouse_name: warehouse.label,
                    zone_id: zone._id,
                    zone_name: zone.label,
                    rack_id: rack._id,
                    rack_name: rack.label,
                    bin_id: bin._id,
                    bin_name: bin.label,
                };
            })
            .filter(Boolean) as Combo[];
    }, [warehouses, zones, racks, bins]);

    const binOptions = combinations?.map((c: Combo) => ({
        value: c.bin_id,
        label: `${c.warehouse_name} > ${c.zone_name} > ${c.rack_name} > ${c.bin_name}`,
        bin: c
    }));

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: `${address.label}: ${address.address_details.addressLine1}, ${address.address_details.city}, ${address.address_details.state}, ${address.address_details.zip ? address.address_details.zip : ''}`,
            }))
            : [];

    const priorityOptions = [
        { value: 1, label: "Critical" },
        { value: 2, label: "High Priority" },
        { value: 3, label: "Normal" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] = useState<Addresses | null>(null);

    const [selectedInstallationAddress, setSelectedInstallationAddress] = useState<Addresses | null>(null);
    const [selectedRemovalAddress, setSelectedRemovalAddress] = useState<Addresses | null>(null);

    // Warehouses/zones/racks/bins only needed for restock (binOptions lookup) at status 500/600
    const warehouseHierarchyFetchedRef = useRef(false);
    useEffect(() => {
        const status = removalOrder?.status;
        if ((status !== 500 && status !== 600) || !token) return;
        if (warehouseHierarchyFetchedRef.current) return;

        let cancelled = false;
        const fetchWarehouseHierarchy = async () => {
            try {
                const headers = {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                };
                const baseUrl = process.env.REACT_APP_API_URL;
                const [warehousesResponse, zonesResponse, racksResponse, binsResponse] = await Promise.all([
                    fetch(`${baseUrl}/warehouses`, { method: "GET", headers }),
                    fetch(`${baseUrl}/zones`, { method: "GET", headers }),
                    fetch(`${baseUrl}/racks`, { method: "GET", headers }),
                    fetch(`${baseUrl}/bins`, { method: "GET", headers }),
                ]);

                if (cancelled) return;

                const [warehousesData, zonesData, racksData, binsData] = await Promise.all([
                    warehousesResponse.json(),
                    zonesResponse.json(),
                    racksResponse.json(),
                    binsResponse.json(),
                ]);

                if (cancelled) return;

                setWarehouses(warehousesData || []);
                setZones(zonesData || []);
                setRacks(racksData || []);
                setBins(binsData || []);
                warehouseHierarchyFetchedRef.current = true;
            } catch (err) {
                if (cancelled) return;
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_all_dropdowns",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/warehouses`,
                    method: "GET",
                }, token);
            }
        };

        fetchWarehouseHierarchy();
        return () => {
            cancelled = true;
        };
    }, [removalOrder?.status, token]);

    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                safeConsole.log("Fetched Addresses:", data);
                setAddressObjects(data);
            } catch (err) {
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_addresses",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                    method: "GET",
                }, token);
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAddresses();
    }, [token]);

    useEffect(() => {
        const fetchRemovalOrder = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    showError(data.error || "Failed to fetch removal order");
                    return;
                }

                const normalizedLineItems = (data.line_items || []).map(normalizeRemovalLineItemFromApi);

                setRemovalOrder({ ...data, line_items: normalizedLineItems });
                setUpdatedProducts(normalizedLineItems);
                safeConsole.log("Fetched removal order:", data);
                const initialQuantities: Record<string, number> = {};
                data.line_items?.forEach((item: any) => {
                    if (item.inventory_source !== "rotational") {
                        initialQuantities[item.product_id] = item.quantity_to_be_removed; // default quantity
                    }
                });
                setRemovalQuantities(initialQuantities);
            } catch (err) {
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_removal_order",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    method: "GET",
                    additionalInfo: { removalOrderId: id },
                }, token);
            }
        };

        if (id) {
            fetchRemovalOrder();
        }
    }, [id]);

    useEffect(() => {
        if (removalOrder?.removal_details?.removal_address?.address_details?.latitude && removalOrder?.removal_details?.removal_address?.address_details?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.address_details.latitude,
                lng: removalOrder.removal_details.removal_address.address_details.longitude,
            });
        }
    }, [removalOrder]);

    // ✅ Map backend special fees to UI format (similar to reference file)
    const preselectedSpecialFees = useMemo(() => {
        // Priority 1: Root-level specialfee (newer format)
        let list = Array.isArray(removalOrder?.specialfee)
            ? removalOrder.specialfee
            : [];

        return list.map((f: any, i: number) =>
            mapSavedInstallationSpecialFeeToUi({
                ...f,
                id: f?.specialfee_id ?? f?.id ?? f?._id ?? `fee-${i}`,
            })
        );
    }, [removalOrder]);

    // ✅ Initial totals for Cost Breakdown from backend payload
    const initialTotals = useMemo(() => {
        const list = Array.isArray(removalOrder?.specialfee) ? removalOrder.specialfee : [];
        const feeSub = list.reduce((s: number, f: any) => s + Number(f?.specialfee_amount ?? f?.amount ?? 0), 0);
        const feeTax = list.reduce((s: number, f: any) => s + Number(f?.specialfee_tax_amount ?? f?.tax_amount ?? 0), 0);
        const removalSub = Number(removalOrder?.removal_cost || 0);
        const removalTax = Number(removalOrder?.removal_tax_amount || 0);
        const grand = removalSub + feeSub + removalTax + feeTax;
        return {
            removal_subtotal: removalSub,
            removal_tax_amount: removalTax,
            specialfee_subtotal: feeSub,
            specialfee_tax_amount: feeTax,
            over_all_tax_amount: removalTax + feeTax,
            grand_total: grand,
            subtotal_amount: Number(removalOrder?.subtotal_amount || 0),
            cartId: String(removalOrder?.cart_id || ""),
        };
    }, [removalOrder]);

    // defult removal address set
    useEffect(() => {
        if (removalOrder) {
            const addr = removalOrder?.removal_details?.removal_address;
            setSelectedRemovalAddress(addr);
        }
        if (removalOrder?.billing_address) {
            setSelectedBillingAddress(removalOrder?.billing_address)
        }

        // ✅ Initialize removal cost from backend
        if (removalOrder) {
            setRemovalCost(removalOrder?.removal_cost || 0);
        }
    }, [removalOrder]);

    // ✅ Seed special fees and totals from backend when order loads
    useEffect(() => {
        if (!removalOrder) return;

        // ✅ In view mode, always update from backend to reflect latest tax data
        // In edit mode, only initialize once to prevent overwriting user changes
        const shouldUpdate = !isEdit || !seededSpecialFeesRef.current;

        if (shouldUpdate) {
            // ✅ Update special fees
            if (preselectedSpecialFees.length > 0) {
                formik.setFieldValue("specialfee", preselectedSpecialFees, false);
                setSelectedSpecialFees(preselectedSpecialFees);
            } else {
                // ✅ Explicitly set empty array if no special fees exist
                setSelectedSpecialFees([]);
                formik.setFieldValue("specialfee", [], false);
            }

            // ✅ Always update totals from backend (includes tax data)
            // Set individual fields to ensure formik properly updates and triggers re-render
            formik.setFieldValue("totals.removal_subtotal", initialTotals.removal_subtotal || 0, false);
            formik.setFieldValue("totals.removal_tax_amount", initialTotals.removal_tax_amount || 0, false);
            formik.setFieldValue("totals.specialfee_subtotal", initialTotals.specialfee_subtotal || 0, false);
            formik.setFieldValue("totals.specialfee_tax_amount", initialTotals.specialfee_tax_amount || 0, false);
            formik.setFieldValue("totals.grand_total", initialTotals.grand_total || 0, false);
            if (initialTotals.cartId) {
                formik.setFieldValue("totals.cartId", initialTotals.cartId, false);
            }

            // ✅ Mark as seeded only in edit mode to allow view mode updates
            if (isEdit) {
                seededSpecialFeesRef.current = true;
            }
        }
    }, [removalOrder, preselectedSpecialFees, initialTotals, isEdit]);

    // Fetch dynamic removal cost in edit mode
    useEffect(() => {
        const fetchRemovalCost = async () => {
            const removalAddress = removalOrder?.removal_details?.removal_address;
            if (
                !removalAddress?.address_details?.latitude ||
                !removalAddress?.address_details?.longitude ||
                removalAddress?.address_details?.latitude === 0 ||
                removalAddress?.address_details?.longitude === 0
            ) {
                safeConsole.log("Removal address missing lat/long");
                return;
            }

            const removalCostPayload = {
                removal_address: {
                    addressLine1: removalAddress?.address_details?.addressLine1,
                    addressLine2: removalAddress?.address_details?.addressLine2,
                    city: removalAddress?.address_details?.city,
                    state: removalAddress?.address_details?.state,
                    country: removalAddress?.address_details?.country,
                    zip: removalAddress?.address_details?.zip,
                    cityCode: removalAddress?.address_details?.cityCode,
                    stateCode: removalAddress?.address_details?.stateCode,
                    countryCode: removalAddress?.address_details?.countryCode,
                    latitude: removalAddress?.address_details?.latitude,
                    longitude: removalAddress?.address_details?.longitude,
                }
            };

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/dynamic_cost/${removalOrder?.signage_id}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(removalCostPayload),
                    }
                );

                const data = await response.json();
                if (!response.ok) throw new Error(data?.error || "Failed to fetch removal cost");
                setDynamicCostId(data.id);
                setDynamicTaxable(data.is_taxable || data.taxable);
                // setRemovalCost(data.cost || 0);
            } catch (error: any) {
                await handleApiError(error, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_removal_cost_dynamic",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/removal_orders/dynamic_cost/${removalOrder?.signage_id}`,
                    method: "POST",
                    requestBody: removalCostPayload,
                }, token);
            }
        };

        if (removalOrder && isEdit) {
            fetchRemovalCost();
        }
    }, [removalOrder, isEdit]);

    // Populate uploadedFileMeta from removalOrder.proof_of_removal
    useEffect(() => {
        if (removalOrder?.proof_of_removal && Array.isArray(removalOrder.proof_of_removal)) {
            const proofFiles: ProofFile[] = removalOrder.proof_of_removal.map((file: any) => ({
                file_name: file.file_name || "",
                file_url: file.file_url || "",
                content_type: file.content_type || "",
                uploaded_date: file.uploaded_date || new Date().toISOString()
            }));
            setUploadedFileMeta(proofFiles);
        }
    }, [removalOrder]);

    // Monitor status changes for Scheduled (300) and Completed (500) to send emails
    const prevStatusRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!removalOrder || !removalOrder.status) return;

        const currentStatus = removalOrder.status;
        const prevStatus = prevStatusRef.current;

        // Only send email if status changed to 300 or 500
        if ((currentStatus === 300 || currentStatus === 500) && prevStatus !== currentStatus && prevStatus !== undefined) {
            (async () => {
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
                    if (from && id) {
                        const { fetchUserData } = await import("utils/userDataFetcher");

                        // Format order number
                        const orderNumber = removalOrder?.order_number || id;

                        // Format date
                        const scheduledDate = removalOrder?.removal_details?.scheduled_date
                            ? new Date(removalOrder.removal_details.scheduled_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                            : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

                        // Get supplier name
                        const supplierId = removalOrder?.signage_id;
                        let signageCompanyName = "Signage Company";
                        if (supplierId) {
                            try {
                                const supplierData = await fetchUserData(supplierId, token);
                                signageCompanyName = supplierData?.companyName || supplierData?.firmDetails?.companyName || signageCompanyName;
                            } catch (err) {
                                safeConsole.warn("Failed to fetch supplier data:", err);
                            }
                        }

                        // Format property address
                        const removalAddr = removalOrder?.removal_details?.removal_address?.address_details;
                        const propertyAddress = removalAddr
                            ? `${removalAddr.addressLine1 || ""}, ${removalAddr.city || ""}, ${removalAddr.state || ""} ${removalAddr.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                            : "N/A";

                        // Get agent name
                        const agentName = removalOrder?.agent_name || removalOrder?.user_name || "Agent";
                        const agentFirstName = agentName.split(' ')[0] || agentName;

                        // Build base URL
                        const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                        const viewOrdersUrl = `${baseUrl}/agent/jobs/removalRequests`;
                        const viewOrderUrl = `${baseUrl}/agent/jobs/viewRemovalRequest/${id}`;

                        const triggerByStatus: Record<number, "Agent Removal Order Scheduled" | "Agent Removal Order Completed"> = {
                            300: "Agent Removal Order Scheduled",
                            500: "Agent Removal Order Completed",
                        };
                        const mappedTrigger = triggerByStatus[currentStatus];
                        if (mappedTrigger) {
                            await sendRemovalOrderTriggerEmail({
                                triggerName: mappedTrigger,
                                removalOrderId: String(removalOrder?._id || id || ""),
                                token: token || "",
                                fromEmail: from,
                            });
                        }
                    }
                } catch (emailError) {
                    safeConsole.error("Failed to send removal status change emails:", emailError);
                }
            })();
        }

        prevStatusRef.current = currentStatus;
    }, [removalOrder?.status, removalOrder, id, token]);

    // Fetch final submitted items when removalOrder status is 600
    useEffect(() => {
        if (removalOrder?.status === 600 && removalOrder?._id) {
            fetchFinalSubmittedItems(removalOrder._id);
        }
    }, [removalOrder?.status, removalOrder?._id]);

    // Populate UI fields with final submitted items data
    useEffect(() => {
        if (finalSubmittedItems && updatedProducts && updatedProducts.length > 0) {
            populateFieldsFromFinalSubmittedItems();
        }
    }, [finalSubmittedItems]);

    // Initialize updatedRotationalUnits with bin location data from serial_allocations
    useEffect(() => {
        if (!updatedProducts || updatedProducts.length === 0) return;

        setUpdatedRotationalUnits((prevUnits) => {
            const initializedUnits: Record<string, any> = { ...prevUnits };
            let hasChanges = false;

            updatedProducts
                .filter((item: any) => item.inventory_source === "rotational" && Array.isArray(item.serial_allocations))
                .forEach((item: any) => {
                    item.serial_allocations?.forEach((serial: any) => {
                        if (serial.serial_id && !prevUnits[serial.serial_id]) {
                            const binLocationDisplay = [
                                serial.warehouse_name,
                                serial.zone_name,
                                serial.rack_name,
                                serial.bin_name
                            ].filter(Boolean).join(' > ');

                            initializedUnits[serial.serial_id] = {
                                current_status: item.current_status || "",
                                restock_quantity: 0,
                                writeoff_quantity: 0,
                                damaged_quantity: 0,
                                reason_to_writeoff: "",
                                repair: 0,
                                bin_location: serial.bin_id || "",
                                bin_location_display: binLocationDisplay,
                                note: "",
                            };
                            hasChanges = true;
                        }
                    });
                });

            return hasChanges ? initializedUnits : prevUnits;
        });
    }, [updatedProducts]);

    const DEFAULT_TEN_ZEROS = "0000000000";
    const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
        !v || v.trim() === "" || v === def ? def : v;

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userEmail: removalOrder?.user_email,
            userPhone: removalOrder?.user_phone,
            order_number: removalOrder?.order_number,
            priority: removalOrder?.priority || 0,
            scheduled_date: removalOrder?.removal_details?.scheduled_date.split("T")[0] || "",
            scheduled_jobs: 0,
            restock_warehouse: "",
            installeId: removalOrder?.removal_details?.installer_id,
            billing_address: "",
            officeId: removalOrder?.office?._id,
            removal_address: "",
            isSameAsBilling: !!removalOrder?.both_address_same,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.removal_details?.removal_address?.label,
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
            localLawsChecked: false,
            partial_amount: 0,
            transactionId: "",
            paymentMethod: "",
            installation_charge: removalOrder?.removal_cost,
            discount_amount: removalOrder?.discount_amount || 0,
            discount_code: removalOrder?.discount_code || "",
            gatecode: removalOrder?.gate_access_code,
            gateCodeEnabled: false,
            subtotal: 0,
            total: 0,
            tax: 0,
            label_of_address: "",
            firstName: "",
            lastName: "",
            phone: DEFAULT_TEN_ZEROS || "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zipcode: "",
            country: "",
            company: "",
            specialfee: (() => {
                const list = Array.isArray(removalOrder?.specialfee) ? removalOrder.specialfee : [];
                return list.map((f: any, i: number) => ({
                    id: String(f?.specialfee_id ?? f?.id ?? f?._id ?? `fee-${i}`),
                    name: String(f?.specialfee_name ?? f?.name ?? `Special Fee ${i + 1}`),
                    amount: Number(f?.specialfee_amount ?? f?.amount ?? 0),
                    taxable: (f?.specialfee_taxable ?? f?.taxable) !== false,
                    tax_percentage: Number.isFinite(f?.specialfee_tax_percentage ?? f?.tax_percentage)
                        ? Number(f?.specialfee_tax_percentage ?? f?.tax_percentage)
                        : null,
                    tax_amount: Number(f?.specialfee_tax_amount ?? f?.tax_amount ?? 0),
                    tax_rule_id: f?.tax_rule_id ?? null,
                    tax_group_id: f?.tax_group_id ?? null,
                }));
            })(),
            totals: (() => {
                const list = Array.isArray(removalOrder?.specialfee) ? removalOrder.specialfee : [];
                const feeSub = list.reduce((s: number, f: any) => s + Number(f?.specialfee_amount ?? f?.amount ?? 0), 0);
                const feeTax = list.reduce((s: number, f: any) => s + Number(f?.specialfee_tax_amount ?? f?.tax_amount ?? 0), 0);
                const removalSub = Number(removalOrder?.removal_cost || 0);
                const removalTax = Number(removalOrder?.removal_tax_amount || 0);
                const grand = removalSub + feeSub + removalTax + feeTax;
                return {
                    removal_subtotal: removalSub,
                    removal_tax_amount: removalTax,
                    specialfee_subtotal: feeSub,
                    specialfee_tax_amount: feeTax,
                    grand_total: grand,
                    cartId: String(removalOrder?.cart_id || ""),
                };
            })(),
            calc_removal_rate_str: "",
            calc_overall_rate_str: "",
            calc_fee_rate_str: "",
        },
        validationSchema: Yup.object({
            userEmail: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            userPhone: Yup.string()
                .required("Phone number is required"),
            priority: Yup.string()
                .required("Removal priority is required"),

            // ✅ Removal Date must be entered
            scheduled_date: Yup.string()
                .required("Removal date is required"),

            // ✅ Billing Address must be selected
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // installer: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            // ✅ Validate required special fees BEFORE submission
            const requiredFees = allAvailableSpecialFees.filter((f: any) => f.required === true);
            const selectedFeeIds = selectedSpecialFees.map((f: any) => String(f.id));
            const missingRequiredFees = requiredFees.filter((f: any) => !selectedFeeIds.includes(String(f.id || f._id)));

            if (missingRequiredFees.length > 0) {
                const feeNames = missingRequiredFees.map((f: any) => f.name).join(", ");
                showError(`The following required special fees must be selected: ${feeNames}`);
                return; // Don't submit
            }

            setFormSubmitting(true);
            try {
                await recompute({ action: "draft" });

                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;

                // ✅ Map special fees for payload
                const uiFees = selectedSpecialFees || [];
                const feeRows = uiFees.map((f) =>
                    mapInstallationSpecialFeeForSubmit(f, { catalog: allAvailableSpecialFees })
                );

                // Build just before your PUT call
                const sourceLineItems =
                    updatedProducts.length > 0 ? updatedProducts : removalOrder?.line_items ?? [];
                const updatedLineItems = sourceLineItems.map((item: any) => {
                    // ✅ Do not touch rotational rows
                    if (item.inventory_source === "rotational") {
                        return withRemovalLineItemIdForPayload(item);
                    }
                    if (item.inventory_source === "custom") {
                        return withRemovalLineItemIdForPayload({
                            ...item,
                            quantity_to_be_removed: 0,
                        });
                    }

                    const originalToRemove = Number(item.quantity_to_be_removed) || 0;
                    const baseAvailable = Number(item.available_quantity) || 0;
                    const pool = baseAvailable + originalToRemove;

                    // Only update if the user changed this product's qty
                    const plannedRaw = removalQuantities[item.product_id];
                    if (plannedRaw == null || Number(plannedRaw) === originalToRemove) {
                        return withRemovalLineItemIdForPayload(item);
                    }

                    // Clamp between 0 and pool
                    const nextQty = Math.max(0, Math.min(Number(plannedRaw) || 0, pool));
                    const nextAvail = Math.max(0, pool - nextQty);

                    return withRemovalLineItemIdForPayload({
                        ...item,
                        quantity_to_be_removed: nextQty,
                        available_quantity: nextAvail,
                    });
                });

                const patchBody = overlayRemovalSubmitFromResolved(
                    {
                        line_items: updatedLineItems,
                        specialfee: feeRows,
                        removal_tax_amount: Number(formik.values.totals.removal_tax_amount || 0),
                        removal_tax_percentage: formik.values.calc_removal_rate_str || "",
                        over_all_tax_amount: Number(formik.values.totals.removal_tax_amount || 0) + Number(formik.values.totals.specialfee_tax_amount || 0),
                        over_all_tax_percentage: formik.values.calc_overall_rate_str || "",
                        discount_amount: Number(formik.values.discount_amount || 0),
                        discount_code: formik.values.discount_code || "",
                        subtotal_amount: Number(formik.values.totals.removal_subtotal || 0) + Number(formik.values.totals.specialfee_subtotal || 0),
                        total_amount: Number(formik.values.totals.grand_total || 0),
                        gate_access_code: formik.values.gatecode || "",
                        removal_details: {
                            scheduled_date: new Date(values.scheduled_date).toISOString(),
                            removal_address: removalOrder?.removal_details?.removal_address,
                            installer_id: removalOrder?.removal_details?.installer_id,
                            installer_name: removalOrder?.removal_details?.installer_name,
                            contact_number: removalOrder?.removal_details?.contact_number,
                            estimated_duration: null,
                            actual_installation_date: null,
                            additional_instructions: [
                                {
                                    value: values.instructions || "",
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                            assignee_id: removalOrder?.removal_details?.installer_id,
                            tax_details: null,
                        },
                        billing_address: selectedBillingAddress,
                        user_email: values.userEmail,
                        user_phone: values.userPhone,
                        both_address_same: values.isSameAsBilling,
                        priority: Number(values.priority),
                    },
                    lastResolvedRemovalRef.current
                );

                const response = await axios.patch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    patchBody,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.status === 200) {
                    const data = response.data;
                    showSuccess(data.message || "Removal request updated successfully!");

                    // ✅ Trigger 6: Agent Removal Order Edit (by Signage) - Send notifications
                    try {
                        const { createNotification } = await import("../../services/notificationApi");
                        const { getAgentRemovalOrderEditContent } = await import("../../services/notificationContent");
                        const { fetchUserData } = await import("../../utils/userDataFetcher");

                        // Fetch updated order data
                        const orderResponse = await fetch(
                            `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                            {
                                method: "GET",
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                    "Content-Type": "application/json",
                                },
                            }
                        );

                        if (orderResponse.ok) {
                            const orderData = await orderResponse.json();
                            const orderNumber = orderData?.order_number || id;

                            // Get current user's license type to determine if they're signage
                            let currentUserLicenseType = "AGENT";
                            if (userId) {
                                try {
                                    const currentUserData = await fetchUserData(userId, token || "");
                                    currentUserLicenseType = currentUserData?.licenseType || "AGENT";
                                } catch (err) {
                                    safeConsole.warn("Failed to fetch current user data:", err);
                                }
                            }

                            // Use userId (the agent who created the order) as end user
                            const endUserId = userId || orderData?.agent_id || "";
                            let supplierName = "Supplier";

                            // Get selected installer ID (check state first, then order data)
                            const installerId = selectedInstaller?.value || orderData?.removal_details?.installer_id || orderData?.installation_details?.installer_id || orderData?.signage_id;

                            if (installerId) {
                                try {
                                    const supplierData = await fetchUserData(installerId, token || "");
                                    supplierName = supplierData?.companyName || supplierData?.firmDetails?.companyName || supplierName;
                                } catch (err) {
                                    safeConsole.warn("Failed to fetch supplier data:", err);
                                }
                            }

                            // Notify End User (Agent)
                            if (endUserId && token) {
                                const agentContent = getAgentRemovalOrderEditContent({
                                    orderId: orderNumber,
                                    supplierName,
                                    recipientType: "agent",
                                });
                                await createNotification(endUserId, agentContent, token, "agent");
                            }

                            // Notify Selected Installer (Signage Admin/Team Member)
                            if (installerId && token) {
                                try {
                                    const installerData = await fetchUserData(installerId, token || "");
                                    const installerLicenseType = installerData?.licenseType || "SIGNAGE_FIRM";
                                    const installerRecipientType = installerLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                                    const installerContent = getAgentRemovalOrderEditContent({
                                        orderId: orderNumber,
                                        supplierName,
                                        recipientType: installerRecipientType,
                                    });
                                    await createNotification(installerId, installerContent, token, installerRecipientType);
                                } catch (err) {
                                    safeConsole.warn(`Failed to send notification to selected installer ${installerId}:`, err);
                                }
                            }
                        }
                    } catch (notifError) {
                        safeConsole.warn("Failed to send removal order edit notification:", notifError);
                        // Don't block the update if notification fails
                    }

                    // Send email notifications for removal order edit
                    try {
                        const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
                        if (from && id) {
                            // Fetch updated order data
                            let orderResponse;
                            try {
                                orderResponse = await fetch(
                                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                                    {
                                        method: "GET",
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            "Content-Type": "application/json",
                                        },
                                    }
                                );
                            } catch (err) {
                                await handleApiError(err, {
                                    module: "removal",
                                    action: "Agent_View_Removal_Request_fetch_order_for_edit_email",
                                    userId: userId || undefined,
                                    licenseType: licenseType || undefined,
                                    userName: userName || undefined,
                                    url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                                    method: "GET",
                                }, token);
                                throw err;
                            }

                            if (orderResponse.ok) {
                                const orderData = await orderResponse.json();
                                const { fetchUserData } = await import("utils/userDataFetcher");

                                await sendRemovalOrderTriggerEmail({
                                    triggerName: "Agent Removal order edit",
                                    removalOrderId: String(orderData?._id || id || ""),
                                    token: token || "",
                                    fromEmail: from,
                                });
                            }
                        }
                    } catch (emailError) {
                        safeConsole.error("Failed to send removal edit emails:", emailError);
                    }

                    // Navigate to view mode with a small delay to ensure the success message is shown
                    setTimeout(() => {
                        safeNavigate(`/agent/jobs/viewRemovalRequest/${id}`, { state: { type: "View" }, replace: true });
                        // Reload the page to ensure fresh data is loaded and avoid version mismatch errors
                        window.location.reload();
                    }, 500);
                } else {
                    throw new Error("Failed to update removal request");
                }
            } catch (error: any) {
                const userEmailError = error?.response?.data?.userEmail;
                const userPhoneError = error?.response?.data?.userPhone;

                if (userEmailError) {
                    formik.setFieldError("userEmail", userEmailError);
                    formik.setFieldTouched("userEmail", true, false);
                }

                if (userPhoneError) {
                    formik.setFieldError("userPhone", userPhoneError);
                    formik.setFieldTouched("userPhone", true, false);
                }

                if (userEmailError || userPhoneError) {
                    setFormSubmitting(false);
                    setUploadProgress(0);
                    return;
                }

                safeConsole.error(error);
                showError(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    useEffect(() => {
        if (formik.submitCount < 1) return;

        formik.setTouched(
            {
                ...formik.touched,
                userEmail: true,
                userPhone: true,
            },
            false
        );
    }, [formik.submitCount]);

    const finalCartId = React.useMemo(() => {
        if (!ordernumber || !removalOrder?.cart_id) return "";
        const suffix = ordernumber.includes("-") ? ordernumber.split("-")[1] : "";
        return suffix ? `${removalOrder.cart_id}-${suffix}` : removalOrder.cart_id;
    }, [ordernumber, removalOrder?.cart_id]);

    type RecomputeOverride = {
        fees?: any[];
        discountCode?: string;
        action?: OrderResolveAction;
    };

    const isRecomputingRef = React.useRef(false);

    const recompute = React.useCallback(async (override?: RecomputeOverride): Promise<boolean | undefined> => {
        if (!dynamicCostId || !token) return false;

        if (isRecomputingRef.current) {
            safeConsole.log("⏭️ Skipping recompute - already in progress");
            return false;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
            pendingOverrideRef.current = null;
        }

        try {
            isRecomputingRef.current = true;
            setIsRecomputing(true);
            const fees = override?.fees ?? getSelectedFees();
            const agentDisplayName =
                `${formik.values.firstName || ""} ${formik.values.lastName || ""}`.trim() ||
                userName ||
                "";
            const discountCode =
                override?.discountCode !== undefined
                    ? String(override.discountCode).trim()
                    : String(formik.values.discount_code || removalOrder?.discount_code || "").trim();

            const resolvePayload = buildRemovalResolvePayload({
                orderNumber: String(removalOrder?.order_number || "").trim(),
                cartId: String(finalCartId || removalOrder?.cart_id || ""),
                dynamicCostId,
                signageId: String(removalOrder?.signage_id || ""),
                sourceId: String(userId || removalOrder?.agent_id || ""),
                customerId: String(userId || removalOrder?.agent_id || ""),
                inventorySource: "agent",
                removalCost: Number(removalCost ?? formik.values.installation_charge ?? 0),
                removalTaxable: dynamicTaxable,
                discountCode,
                paymentMethod: formik.values.payment_method || "card",
                userType: removalOrder?.user_type,
                userName: agentDisplayName,
                userEmail: formik.values.userEmail,
                userPhone: formik.values.userPhone,
                agentId: userId || null,
                agentName: agentDisplayName,
                billingAddress: selectedBillingAddress as InstallationResolveAddress | null,
                removalAddress: removalOrder?.removal_details?.removal_address as InstallationResolveAddress | null,
                installerId: String(removalOrder?.removal_details?.installer_id || ""),
                installerName: String(removalOrder?.removal_details?.installer_name || ""),
                lineItems: [],
                specialFees: fees.map((f: any) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
            });

            const resolveAction: OrderResolveAction = override?.action ?? "draft";
            const resolved = await postRemovalOrderResolve(token, {
                order_type: "removal",
                action: resolveAction,
                payload: resolvePayload,
            });

            if (!resolved || typeof resolved !== "object") {
                const allFeesSubtotal = fees.reduce((sum: number, f: any) => sum + Number(f.amount || 0), 0);
                formik.setFieldValue("totals.removal_subtotal", Number(removalCost) || 0, false);
                formik.setFieldValue("totals.removal_tax_amount", 0, false);
                formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
                formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                formik.setFieldValue("totals.grand_total", (Number(removalCost) || 0) + allFeesSubtotal, false);
                formik.setFieldValue("specialfee", fees, false);
                return false;
            }

            lastResolvedRemovalRef.current = resolved;
            const applied = applyRemovalWorkOrderResolveResponse(
                resolved,
                formik,
                Number(removalCost) || 0,
                {
                    requestedDiscountCode: discountCode,
                    existingFeeRows: fees,
                    specialFeeCatalog: allAvailableSpecialFees,
                }
            );
            setSelectedSpecialFees(applied.mergedFees);
            return true;
        } catch (e) {
            safeConsole.error("[OrderResolve] agent removal view failed:", e);
            return false;
        } finally {
            isRecomputingRef.current = false;
            setIsRecomputing(false);
        }
    }, [
        token,
        userId,
        userName,
        removalOrder,
        removalCost,
        dynamicCostId,
        dynamicTaxable,
        finalCartId,
        selectedBillingAddress,
        formik,
        isEdit,
    ]);

    recomputeRef.current = recompute;

    const refreshRemovalOrder = React.useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (response.ok) {
                const normalizedLineItems = (data.line_items || []).map(normalizeRemovalLineItemFromApi);

                setRemovalOrder({ ...data, line_items: normalizedLineItems });
                setUpdatedProducts(normalizedLineItems);
            }
        } catch (err) {
            await handleApiError(err, {
                module: "removal",
                action: "Agent_View_Removal_Request_fetch_removal_order_refresh",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                method: "GET",
            }, token);
        }
    }, [id, token, userId, licenseType, userName]);

    const finalizeTaxAfterPaidPayment = React.useCallback(
        async (options?: { force?: boolean }): Promise<boolean> => {
            if (taxCloudFinalized || taxFinalizeInProgressRef.current) return false;

            const canRunResolve =
                taxType === "taxcloud" &&
                dynamicCostId &&
                finalCartId &&
                removalOrder?.order_number;
            if (!canRunResolve) return false;

            const normalizedTaxStatus = String(removalOrder?.tax_status || "").trim().toLowerCase();
            if (normalizedTaxStatus === "complete") return false;

            if (!options?.force) {
                const isOrderPaid =
                    String(removalOrder?.payment_status || "").trim().toLowerCase() === "paid";
                if (
                    !isOrderPaid ||
                    (normalizedTaxStatus !== "draft" && normalizedTaxStatus !== "")
                ) {
                    return false;
                }
            }

            taxFinalizeInProgressRef.current = true;
            try {
                const resolvedOk = await recomputeRef.current?.({ action: "complete" });
                const resolved = lastResolvedRemovalRef.current;
                if (!resolvedOk || !resolved) return false;

                await patchRemovalOrderTaxComplete(token || "", String(id), resolved, {
                    version: removalOrder?.version,
                    removalCost: Number(
                        removalCost ??
                            formik.values.totals?.removal_subtotal ??
                            removalOrder?.removal_cost ??
                            0
                    ),
                    existingFeeRows: selectedSpecialFees,
                    specialFeeCatalog: allAvailableSpecialFees,
                });

                setTaxCloudFinalized(true);
                showSuccess("TaxCloud order finalized successfully!");
                return true;
            } catch (e) {
                safeConsole.error("[TaxCloud] Paid tax finalize failed:", e);
                return false;
            } finally {
                taxFinalizeInProgressRef.current = false;
            }
        },
        [
            taxCloudFinalized,
            taxType,
            dynamicCostId,
            finalCartId,
            removalOrder?.order_number,
            removalOrder?.tax_status,
            removalOrder?.payment_status,
            removalOrder?.version,
            removalOrder?.removal_cost,
            removalCost,
            formik.values.totals?.removal_subtotal,
            selectedSpecialFees,
            allAvailableSpecialFees,
            token,
            id,
        ]
    );
    finalizeTaxAfterPaidPaymentRef.current = finalizeTaxAfterPaidPayment;

    const [couponInput, setCouponInput] = React.useState("");
    const userClearedCouponRef = React.useRef(false);
    const liveDiscountDisplay = React.useMemo(
        () =>
            getOrderDiscountFieldDisplay({
                formikCode: formik.values.discount_code,
                formikAmount: formik.values.discount_amount,
                couponInput,
                resolved: lastResolvedRemovalRef.current as Parameters<typeof getOrderDiscountFieldDisplay>[0]["resolved"],
            }),
        [formik.values.discount_code, formik.values.discount_amount, couponInput]
    );

    React.useEffect(() => {
        const code = String(removalOrder?.discount_code || "").trim();
        if (code && !couponInput) {
            setCouponInput(code);
        }
    }, [removalOrder?.discount_code]);

    // Helper functions for rotational units
    const getRotationalSum = (serialId: string) => {
        const unit = updatedRotationalUnits[serialId];
        if (!unit) return 0;
        return (unit.restock_quantity || 0) + (unit.writeoff_quantity || 0) + (unit.damaged_quantity || 0) + (unit.repair || 0);
    };

    const shouldDisableRotationalField = (serialId: string, field: string) => {
        // Disable all fields when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return true;
        }

        const unit = updatedRotationalUnits[serialId];
        if (!unit) return false;

        // Exactly one field should be 1, others should be 0 and disabled
        if (unit.restock_quantity === 1) {
            return field === "writeoff_quantity" || field === "damaged_quantity" || field === "repair";
        }
        if (unit.writeoff_quantity === 1) {
            return field === "restock_quantity" || field === "damaged_quantity" || field === "repair";
        }
        if (unit.damaged_quantity === 1) {
            return field === "restock_quantity" || field === "writeoff_quantity" || field === "repair";
        }
        if (unit.repair === 1) {
            return field === "restock_quantity" || field === "writeoff_quantity" || field === "damaged_quantity";
        }
        return false;
    };

    const shouldEnableRotationalBinLocation = (serialId: string) => {
        // Disable bin location selection when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return false;
        }

        const unit = updatedRotationalUnits[serialId];
        // Treat Repair like Restock for bin location requirements
        return unit && (unit.restock_quantity === 1 || unit.repair === 1);
    };

    const updateRotationalUnit = (
        serialId: string,
        field: keyof (typeof updatedRotationalUnits[string]),
        value: any,
        _availableQty: number = 1
    ) => {
        setUpdatedRotationalUnits((prev) => {
            const current = prev[serialId] || {
                current_status: "",
                restock_quantity: 0,
                writeoff_quantity: 0,
                damaged_quantity: 0,
                reason_to_writeoff: "",
                repair: 0,
                bin_location: "",
                bin_location_display: "",
                note: "",
            };

            // For rotational units, exactly one field should be 1, others should be 0
            if (field === "restock_quantity" || field === "writeoff_quantity" || field === "damaged_quantity" || field === "repair") {
                value = value > 0 ? 1 : 0;

                if (value === 1) {
                    return {
                        ...prev,
                        [serialId]: {
                            ...current,
                            restock_quantity: field === "restock_quantity" ? 1 : 0,
                            writeoff_quantity: field === "writeoff_quantity" ? 1 : 0,
                            damaged_quantity: field === "damaged_quantity" ? 1 : 0,
                            repair: field === "repair" ? 1 : 0,
                            [field]: 1,
                        },
                    };
                } else {
                    return {
                        ...prev,
                        [serialId]: {
                            ...current,
                            [field]: 0,
                        },
                    };
                }
            }

            // Special handling for bin_location: also update bin_location_display
            if (field === "bin_location") {
                const binOption = binOptions.find(opt => opt.value === value);
                const binLocationDisplay = binOption?.label || "";

                return {
                    ...prev,
                    [serialId]: {
                        ...current,
                        bin_location: value,
                        bin_location_display: binLocationDisplay,
                    },
                };
            }

            return {
                ...prev,
                [serialId]: {
                    ...current,
                    [field]: value,
                },
            };
        });
    };

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "categories");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const url = `${process.env.REACT_APP_API_URL}/images/upload`;
            xhr.open("POST", url, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = async () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    const error = new Error("Failed to upload file");
                    try {
                        await handleApiError(error, {
                            module: "removal",
                            action: "Agent_View_Removal_Request_upload_file",
                            userId: userId || undefined,
                            licenseType: licenseType || undefined,
                            userName: userName || undefined,
                            url: url,
                            method: "POST",
                            additionalInfo: { status: xhr.status, statusText: xhr.statusText },
                        }, token);
                    } catch (err) {
                        // Error handler error - just log
                        safeConsole.error("Error in handleApiError:", err);
                    }
                    reject(error);
                }
            };

            xhr.onerror = async () => {
                const error = new Error("Network error during file upload");
                try {
                    await handleApiError(error, {
                        module: "removal",
                        action: "Agent_View_Removal_Request_upload_file",
                        userId: userId || undefined,
                        licenseType: licenseType || undefined,
                        userName: userName || undefined,
                        url: url,
                        method: "POST",
                        additionalInfo: { error: "Network error" },
                    }, token);
                } catch (err) {
                    // Error handler error - just log
                    safeConsole.error("Error in handleApiError:", err);
                }
                reject(error);
            };
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

    useEffect(() => {
        if(!removalOrder?.signage_id) return;
        const fetchIntegrations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${removalOrder?.signage_id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch integrations.");
                }
                const data = await response.json();
                safeConsole.log('Methods ', data);
                setIntegrations(data);
            } catch (err) {
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_integrations",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${removalOrder?.signage_id}`,
                    method: "GET",
                }, token);
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, [removalOrder]);

    // Debounced recompute trigger
    const ready = React.useMemo(
        () =>
            Boolean(
                isEdit &&
                removalOrder?.signage_id &&
                removalOrder?.removal_details?.removal_address &&
                Number.isFinite(Number(removalCost))
            ),
        [isEdit, removalOrder?.signage_id, removalOrder?.removal_details?.removal_address, removalCost]
    );

    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingOverrideRef = React.useRef<RecomputeOverride | null>(null);

    const triggerRecomputeWith = React.useCallback((override?: RecomputeOverride) => {
        // ✅ Don't schedule if recompute is already running
        if (isRecomputingRef.current) {
            safeConsole.log("⏸️ Recompute in progress, skipping debounced call");
            return;
        }

        pendingOverrideRef.current = override || null;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            // ✅ Double-check before calling (in case state changed during debounce)
            if (!isRecomputingRef.current) {
                const ov = pendingOverrideRef.current;
                pendingOverrideRef.current = null;
                recompute(ov || undefined);
            }
        }, 300);
    }, [recompute]);

    // ✅ Trigger recompute when removal cost changes
    const prevRemovalCostRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        if (!ready) return;

        // ✅ Only trigger if cost actually changed
        if (prevRemovalCostRef.current === removalCost) return;

        // Skip first render
        if (prevRemovalCostRef.current === null) {
            prevRemovalCostRef.current = removalCost;
            return;
        }

        prevRemovalCostRef.current = removalCost;
        safeConsole.log("🔄 Removal cost changed, triggering tax recalculation");
        triggerRecomputeWith();
    }, [removalCost, ready, triggerRecomputeWith]);

    React.useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

    // ✅ Fetch active tax type on mount
    useEffect(() => {
        (async () => {
            try {
                if (!token || !removalOrder?.signage_id) return;
                const t = await getActiveTaxType(token, removalOrder.signage_id);
                setTaxType(t === "taxcloud" ? "taxcloud" : t === "manual" ? "manual" : "unknown");
                safeConsole.log("[TAX] Active tax type for removal:", t);
            } catch (e) {
                safeConsole.warn("[TAX] getActiveTaxType failed:", e);
                setTaxType("unknown");
            }
        })();
    }, [token, removalOrder?.signage_id]);
    const normalizedPaymentStatus = String(removalOrder?.payment_status || "").trim().toLowerCase();
    const isOrderPaid = normalizedPaymentStatus === "paid";
    const normalizedTaxStatus = String(removalOrder?.tax_status || "").trim().toLowerCase();
    const shouldAutoFinalizeTax =
        isOrderPaid &&
        (normalizedTaxStatus === "draft" || normalizedTaxStatus === "");

    useEffect(() => {
        const shouldFinalize =
            taxType === "taxcloud" &&
            dynamicCostId &&
            finalCartId &&
            removalOrder?.order_number &&
            !taxCloudFinalized;
        if (!shouldFinalize || !shouldAutoFinalizeTax) return;

        let cancelled = false;
        (async () => {
            const ok = await finalizeTaxAfterPaidPayment();
            if (!cancelled && ok) {
                await refreshRemovalOrder();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        taxType,
        dynamicCostId,
        finalCartId,
        removalOrder?.payment_status,
        removalOrder?.order_number,
        removalOrder?.tax_status,
        taxCloudFinalized,
        finalizeTaxAfterPaidPayment,
        refreshRemovalOrder,
        shouldAutoFinalizeTax,
    ]);



    // Sync totals to legacy fields
    React.useEffect(() => {
        const t = formik.values.totals || {};
        const subAll = Number(t.removal_subtotal || 0) + Number(t.specialfee_subtotal || 0);
        const taxAll = Number(t.removal_tax_amount || 0) + Number(t.specialfee_tax_amount || 0);
        const grand = Number(t.grand_total || 0);

        formik.setFieldValue("subtotal", subAll, false);
        formik.setFieldValue("tax", taxAll, false);

        const final = Math.max(0, grand - Number(formik.values.discount_amount || 0));
        formik.setFieldValue("total", final, false);
    }, [
        formik.values.totals.removal_subtotal,
        formik.values.totals.specialfee_subtotal,
        formik.values.totals.removal_tax_amount,
        formik.values.totals.specialfee_tax_amount,
        formik.values.totals.grand_total,
        formik.values.discount_amount,
    ]);

    const handleRemovalStatus = async (nextStatus: number): Promise<boolean> => {
        if (!id || !token) return false;
        setLoading(true);

        let url = "";
        let body: any = undefined;
        let statusUpdatePayload: any = undefined;

        try {

            // const scheduledDate = removalOrder?.removal_details?.scheduled_date;
            const newDate = formik.values.scheduled_date;
            // const isDateChanged = newDate && newDate !== scheduledDate?.split("T")[0];

            switch (nextStatus) {
                case 200: // Finalize
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/finalize`;
                    statusUpdatePayload = undefined;
                    break;
                case 700: // Cancel
                    let refundSuccess = false;
                    let nextTaxStatus = removalOrder?.tax_status || "draft";

                    try {
                        const forceType =
                            removalOrder?.tax_status === "complete" ? "taxcloud" : undefined;

                        refundSuccess = await (
                            installationTaxRef.current?.recalculate?.(forceType, {
                                allowRefund: true,
                            }) ?? false
                        );

                        safeConsole.log("[INSTALL:ORDER_SUBMIT] tax recalc result:", { refundSuccess });

                        // 👉 RUN THIS ONLY IF refund is successful
                        if (refundSuccess) {
                            const order_type = removalOrder?.order_type || "removal";

                            const res = await PostVersionOrdernumber(
                                token,
                                removalOrder?.order_number || "",
                                order_type
                            );

                            safeConsole.log("[INSTALL:ORDER_SUBMIT] TaxCloud refund result:", { res });

                            if (!res) throw new Error("TaxCloud refund failed");

                            nextTaxStatus = "draft";
                        } else {
                            safeConsole.log("[INSTALL:ORDER_SUBMIT] Refund NOT successful → skipping PostVersionOrdernumber");
                        }

                    } catch (err) {
                        safeConsole.error("[INSTALL:ORDER_SUBMIT] tax recalc failed", err);
                    }
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/cancel`;
                    body = {
                        reason: formik.values.instructions || "Cancelled by user",
                    };
                    statusUpdatePayload = body;
                    break;

                // case 800: // Void
                //     url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/void`;
                //     body = {
                //         reason: formik.values.instructions || "Voided by supplier",
                //     };
                //     break;

                default:
                    showError("Invalid status selected");
                    setLoading(false);
                    return false;
            }

            statusUpdatePayload = body;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result?.error || "Status update failed");

            showSuccess(result.message || "Removal status updated");
            setRemovalOrder((prev: any) => ({ ...prev, status: nextStatus }));

            // ✅ Trigger 4: Agent Removal Order Canceled - Send notifications
            if (nextStatus === 700) {
                try {
                    const { createNotification } = await import("../../services/notificationApi");
                    const { getAgentRemovalOrderCanceledContent } = await import("../../services/notificationContent");

                    // Fetch updated order details
                    const orderResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );

                    if (orderResponse.ok) {
                        const orderData = await orderResponse.json();
                        const orderNumber = orderData?.order_number || id;

                        // Notify Agent
                        const agentId = orderData?.agent_id;
                        if (agentId && token) {
                            const agentContent = getAgentRemovalOrderCanceledContent({
                                orderId: orderNumber,
                                recipientType: "agent",
                            });
                            await createNotification(agentId, agentContent, token, "agent");
                        }
                    }
                } catch (notifError) {
                    safeConsole.warn("Failed to send removal status notification:", notifError);
                    // Don't block the status update if notification fails
                }
            }

            // Send email notifications for status changes (Canceled/700, Void/800)
            // if (nextStatus === 700 || nextStatus === 800) {
            if (nextStatus === 700) {
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
                    if (from && id) {
                        // Fetch updated removal order data
                        let orderResponse;
                        try {
                            orderResponse = await fetch(
                                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                                {
                                    method: "GET",
                                    headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                    },
                                }
                            );
                        } catch (err) {
                            await handleApiError(err, {
                                module: "removal",
                                action: "Agent_View_Removal_Request_fetch_order_for_status_email",
                                userId: userId || undefined,
                                licenseType: licenseType || undefined,
                                userName: userName || undefined,
                                url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                                method: "GET",
                            }, token);
                            throw err;
                        }

                        if (orderResponse.ok) {
                            const orderData = await orderResponse.json();
                            const { fetchUserData } = await import("utils/userDataFetcher");

                            // Format order number
                            const orderNumber = orderData?.order_number || id;

                            // Format date
                            const statusDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

                            // Get supplier name
                            const supplierId = orderData?.signage_id;
                            let signageCompanyName = "Signage Company";
                            if (supplierId) {
                                try {
                                    const supplierData = await fetchUserData(supplierId, token);
                                    signageCompanyName = supplierData?.companyName || supplierData?.firmDetails?.companyName || signageCompanyName;
                                } catch (err) {
                                    safeConsole.warn("Failed to fetch supplier data:", err);
                                }
                            }

                            // Format property address
                            const removalAddr = orderData?.removal_details?.removal_address?.address_details;
                            const propertyAddress = removalAddr
                                ? `${removalAddr.addressLine1 || ""}, ${removalAddr.city || ""}, ${removalAddr.state || ""} ${removalAddr.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                                : "N/A";

                            // Get agent name
                            const agentName = orderData?.agent_name || orderData?.user_name || "Agent";
                            const agentFirstName = agentName.split(' ')[0] || agentName;

                            // Build base URL
                            const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                            const viewOrdersUrl = `${baseUrl}/signage/removalRequests`;
                            const submitRemovalUrl = `${baseUrl}/agent/jobs/addRemovalRequest`;

                            const triggerByStatus: Record<number, "Agent Removal Order Canceled" | "Agent Removal Order Void"> = {
                                700: "Agent Removal Order Canceled",
                                800: "Agent Removal Order Void",
                            };
                            const mappedTrigger = triggerByStatus[nextStatus];
                            if (mappedTrigger) {
                                await sendRemovalOrderTriggerEmail({
                                    triggerName: mappedTrigger,
                                    removalOrderId: String(orderData?._id || id || ""),
                                    token: token || "",
                                    fromEmail: from,
                                });
                            }
                        }
                    }
                } catch (emailError) {
                    safeConsole.error("Failed to send removal status emails:", emailError);
                    // Don't block navigation if email fails
                }
            }

            safeNavigate(0); // Refresh the page to reflect changes
            return true;
        } catch (err: any) {
            safeConsole.error("Status update error:", err.message);
            showError(err.message || "Something went wrong");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

    useEffect(() => {
        const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
        safeConsole.log('Stripe info ', stripeInfo);
        if (stripeInfo && stripeInfo.publishable_key) {
            const promise = loadStripe(stripeInfo.publishable_key);
            setStripePromise(promise);
        }
    }, [integrations]);

    // Helper function to send payment applied emails for removal orders
    const sendRemovalPaymentAppliedEmails = async (
        updatedOrder: any,
        paymentAmountValue: number,
        paymentMethodName: string,
        fromEmail: string
    ) => {
        if (!fromEmail || !updatedOrder) {
            safeConsole.warn("sendRemovalPaymentAppliedEmails: Missing fromEmail or updatedOrder", { fromEmail: !!fromEmail, updatedOrder: !!updatedOrder });
            return;
        }

        try {
            safeConsole.log("sendRemovalPaymentAppliedEmails: Starting email send", {
                orderId: updatedOrder._id || updatedOrder.id,
                paymentAmountValue,
                paymentMethodName
            });

            // Import user data fetcher
            const { fetchUserData } = await import("utils/userDataFetcher");

            // Format order ID
            const orderId = `#${updatedOrder.order_number || updatedOrder._id || updatedOrder.id || "REM-XXXX"}`;

            // Get payment details from updated order
            const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
            const latestPayment = paymentInfoArray.length > 0 ? paymentInfoArray[paymentInfoArray.length - 1] : null;

            // Format payment amount - use latest payment if available, otherwise use the passed value
            const paymentAmount = latestPayment?.amount_paid
                ? `$${Number(latestPayment.amount_paid).toFixed(2)}`
                : `$${Number(paymentAmountValue).toFixed(2)}`;

            // Format payment date
            const paymentDate = latestPayment?.payment_date
                ? new Date(latestPayment.payment_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

            // Format payment method
            const formattedPaymentMethod = paymentMethodName === "online"
                ? "Credit Card"
                : paymentMethodName === "bank_transfer"
                    ? "Bank Transfer"
                    : paymentMethodName === "cheque"
                        ? "Cheque"
                        : paymentMethodName;

            // Calculate remaining balance from updated order
            const totalPaidAfter = paymentInfoArray.reduce(
                (acc: number, p: any) => acc + Number(p?.amount_paid || 0),
                0
            );
            const remainingBalance = Math.max(0, Number(updatedOrder.total_amount || 0) - totalPaidAfter);
            const currentBalance = remainingBalance > 0 ? `$${remainingBalance.toFixed(2)}` : "$0.00";

            // Determine who applied the payment - get current user from userId
            const currentUserId = getAuthCookie("userId");
            let currentUserData = null;
            if (currentUserId) {
                currentUserData = await fetchUserData(currentUserId, token);
            }

            const currentUserLicenseType = currentUserData?.licenseType || getAuthCookie("licenseType") || "";
            const currentUserEmail = currentUserData?.email || "";
            const currentUserName = currentUserData?.firstName ? `${currentUserData.firstName} ${currentUserData.lastName || ""}`.trim() : userName || "";

            let userRoleName = "User";
            if (currentUserLicenseType === "SIGNAGE_FIRM") {
                userRoleName = "Signage Admin";
            } else if (currentUserLicenseType === "SIGNAGE_TEAM_MEMBER") {
                userRoleName = "Signage Team Member";
            } else if (currentUserLicenseType === "BROKERAGE_FIRM") {
                userRoleName = "Brokerage Firm Admin";
            } else if (currentUserLicenseType === "BROKERAGE_TEAM_MEMBER") {
                userRoleName = "Brokerage Team Member";
            } else if (currentUserLicenseType === "AGENT" || currentUserLicenseType === "BROKERAGE_AGENT") {
                userRoleName = "Agent";
            }

            const paidByUser = (latestPayment as any)?.paid_by?.name || currentUserName || "User";
            const paidByEmail = (latestPayment as any)?.paid_by?.email || currentUserEmail || "";
            const paymentAppliedBy = `${userRoleName} (${paidByEmail})`;

            // Get agent name and brokerage firm name
            const agentName = updatedOrder.agent_name || updatedOrder.user_name || "N/A";
            const brokerageFirm = updatedOrder.brokerage_firm_name || "N/A";

            // Format property address
            const propertyAddr = updatedOrder.removal_details?.removal_address?.address_details
                ? `${updatedOrder.removal_details.removal_address.address_details.addressLine1 || ""}, ${updatedOrder.removal_details.removal_address.address_details.city || ""}, ${updatedOrder.removal_details.removal_address.address_details.state || ""} ${updatedOrder.removal_details.removal_address.address_details.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                : "N/A";

            // Build base URL for order links
            const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
            const viewOrderUrl = `${baseUrl}/agent/jobs/viewRemovalRequest/${updatedOrder._id || updatedOrder.id}`;

            await sendRemovalOrderTriggerEmail({
                triggerName: "Signage Payment applied for removal Job",
                removalOrderId: String(updatedOrder?._id || updatedOrder?.id || ""),
                token: token || "",
                fromEmail,
                includeOrderPdfAttachment: true,
            });
        } catch (emailError) {
            safeConsole.error("Failed to send payment applied emails:", emailError);
            // Don't block payment success if email fails, but log the error for debugging
        }
    };

    const completeOrder = async (method: { name: string; _id: string }) => {
        safeConsole.log("method", method);
        setLoading(true);
        let paymentSucceeded = false;

        // Find the selected payment method to check is_credit
        const selectedPaymentMethod = paymentMethod.find(pm => pm._id === method._id);
        const isCreditPayment = selectedPaymentMethod?.is_credit || false;

        // Calculate payment amounts based on credit status
        const amountPaid = isCreditPayment ? 0 : remaingamount;
        const amountDue = isCreditPayment ? remaingamount : 0;

        const paymentPayload = {
            payment_id: "",
            total_amount: Number(formik.values.totals.grand_total || 0) - Number(formik.values.discount_amount || 0),
            amount_paid: amountPaid,
            amount_due: amountDue,
            payment_date: new Date().toISOString(),
            method: method.name,
            paid_by: {
                id: userId,
                name: userName,
            },
            payment_status: isCreditPayment ? (removalOrder?.payment_status ?? "Pending") : "Paid",
        };

        try {

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/payment`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(paymentPayload),
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || "Online payment failed.");
            }

            // Wait a moment for backend to process payment before fetching updated order
            await new Promise(resolve => setTimeout(resolve, 500));

            // Fetch updated order to get latest payment info (with retry)
            let updatedOrder = removalOrder;
            let retries = 3;
            while (retries > 0) {
                try {
                    const updatedOrderRes = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (updatedOrderRes.ok) {
                        const fetchedOrder = await updatedOrderRes.json();
                        // Check if payment_info has been updated (has at least one payment)
                        if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                            updatedOrder = fetchedOrder;
                            break;
                        } else if (retries === 1) {
                            // Last retry, use fetched order even if payment_info is empty
                            updatedOrder = fetchedOrder;
                        }
                    }
                } catch (fetchErr) {
                    safeConsole.warn(`Failed to fetch updated order (retries left: ${retries - 1}):`, fetchErr);
                }
                if (retries > 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
                retries--;
            }

            // Send "Signage Payment applied for removal Job" emails after successful payment
            const from = process.env.REACT_APP_FROM_EMAIL2;
            if (from) {
                try {
                    await sendRemovalPaymentAppliedEmails(updatedOrder, amountPaid, method.name, from);
                } catch (emailErr) {
                    safeConsole.error("Failed to send payment emails:", emailErr);
                    // Don't block payment success, but log the error
                }
            }
            // }

            showSuccess("Payment submitted successfully!");
            try {
                await finalizeTaxAfterPaidPaymentRef.current({ force: true });
            } catch (finalizeErr) {
                safeConsole.warn("[TaxCloud] Post-payment finalize failed:", finalizeErr);
            }
            localStorage.removeItem("cart_id");
            paymentSucceeded = true;

        } catch (error) {
            await handleApiError(error, {
                module: "removal",
                action: "Agent_View_Removal_Request_complete_order",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/payment`,
                method: "POST",
                requestBody: paymentPayload,
            }, token);
            const errMessage =
                (error as Error).message || "An unknown error occurred";
            safeConsole.error("Order Failed:", errMessage);
            showError(`Error: ${errMessage}`);
        } finally {
            setLoading(false);
            if (paymentSucceeded) {
                safeNavigate(0);
            }
        }
    };

    const totalPaid = (removalOrder?.payment_info ?? []).reduce(
        (acc: number, payment: { amount_paid?: number | string }) =>
            acc + Number(payment.amount_paid || 0),
        0
    );


    const maxAllowed = (removalOrder?.payment_info ?? []).length > 0 ? Number((Number(removalOrder?.total_amount || 0) - totalPaid).toFixed(2)) : Number(removalOrder?.total_amount || 0);

    const totalAmountNum =
        Number(removalCost || 0) +
        Number(formik.values?.totals?.specialfee_subtotal || 0) +
        Number(formik.values?.totals?.removal_tax_amount || 0) +
        Number(formik.values?.totals?.specialfee_tax_amount || 0) -
        Number(formik.values?.discount_amount || 0);

    const TotalAmount = totalAmountNum.toFixed(2);              // string for UI
    const remaingamount = +(totalAmountNum - Number(totalPaid || 0)).toFixed(2); // number (rounded)


    // const remaingamount = ((removalOrder?.total_amount || 0) - totalPaid)

    const isFieldEditable = (status: number, field: string): boolean => {
        const editableMap: Record<number, string[]> = {
            100: [
                // Draft
                "BillingAddress",
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
                "userEmail",
                "userPhone",
            ],
            // 200: [
            //     // Payment Accepted (Finalized)
            //     "ScheduleDate",
            //     "Installer",
            //     "Priority",
            //     "Notes",
            //     "Instructions",
            //     "removalQuantity",
            //     "userEmail",
            //     "userPhone",
            // ],
            // 300: [
            //     // Scheduled or Rescheduled
            //     "ScheduleDate",
            //     "Installer",
            //     // "Priority",
            //     "Notes",
            //     "Instructions",
            //     "removalQuantity",
            //     // "userEmail",
            //     // "userPhone",
            // ],
            // 400: [
            //     // In Progress
            //     "Instructions", // Only instructions editable
            // ],
            // 500 and 600: No edits allowed
        };

        const nonEditableStatuses = [200, 300, 400, 500, 600, 700, 800]; // Completed, Canceled

        if (nonEditableStatuses.includes(status)) return false;

        const editableFields = editableMap[status] || [];
        return editableFields.includes(field);
    };

    const statusHistory: any[] = Array.isArray(removalOrder?.status_history)
        ? (removalOrder?.status_history as any[])
        : [];
    // safeConsole.log("statusHistory", statusHistory);
    const paymentInfo: PaymentInfoItem[] = [...(removalOrder?.payment_info || [])];

    const resolveOrderStatus = (status: number | undefined): string => {
        switch (status) {
            case 100: return "Draft";
            case 200: return "Finalized";
            case 300: return "Scheduled";
            case 400: return "In Progress";
            case 500: return "Completed";
            case 600: return "Canceled";
            case 700: return "Losted";
            default: return status !== undefined ? `Status ${status}` : "Unknown";
        }
    };


    const parseDate = (input: any): string => {
        try {
            return new Date(input).toLocaleString();
        } catch {
            return "Invalid Date";
        }
    };

    useEffect(() => {
        const fetchPaymentIntent = async () => {
            // const amountToPay = (formik.values.partial_amount === 0
            //   ? (Number(order?.total_amount || 0) - totalPaid)
            //   : formik.values.partial_amount);

            safeConsole.log("amountToPay", (formik.values.partial_amount === 0
                ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                : formik.values.partial_amount));

            const paymentIntentPayload = {
                amount: Math.round(
                    Number(
                        formik.values.partial_amount === 0
                            ? Number(removalOrder?.total_amount || 0) - totalPaid
                            : formik.values.partial_amount
                    ) * 100
                ),
                currency: "usd",
                order_id: String(removalOrder?._id || removalOrder?.id || ""),
                order_number: String(removalOrder?.order_number || ""),
                customer_name: String(removalOrder?.user_name || ""),
                customer_email: String(removalOrder?.user_email || ""),
                customer_phone: String(removalOrder?.user_phone || ""),
                payment_method_types: ["card"],
            };

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${removalOrder?.signage_id}`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(paymentIntentPayload),
                    }
                );
                const data = await res.json();
                setClientSecret(data.clientSecret || data.client_secret);
            } catch (err) {
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_payment_intent",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${removalOrder?.signage_id}`,
                    method: "POST",
                    requestBody: paymentIntentPayload,
                }, token);
            }
        };

        if (stripePromise) fetchPaymentIntent();
    }, [
        stripePromise,
        formik.values.partial_amount,
        removalOrder?.total_amount,
        removalOrder?._id,
        removalOrder?.id,
        removalOrder?.order_number,
        removalOrder?.user_name,
        removalOrder?.user_email,
        removalOrder?.user_phone,
        totalPaid,
    ]);

    const coalesceDestination = (): Addresstax => {
        const a = removalOrder?.removal_details?.removal_address?.address_details as Addresstax | undefined;
        return {
            addressLine1: a?.addressLine1 ?? "",
            city: a?.city ?? "",
            state: a?.stateCode ?? a?.state ?? "",
            zip: a?.zip ?? "",
            stateCode: a?.stateCode ?? a?.state ?? "",
        };
    };

    // ✅ Get selected fees from state
    const getSelectedFees = React.useCallback(() => {
        return selectedSpecialFees
            .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
            .filter((f) => f.id !== "");
    }, [selectedSpecialFees, allAvailableSpecialFees]);

    const typeEditMode = !isEdit

    // Function to update gate access code only
    const handleSaveGateCode = async () => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    version: removalOrder?.version,
                    gate_access_code: formik.values.gatecode,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                showSuccess("Gate access code updated successfully!");
                // Update the local state to reflect the change
                if (removalOrder) {
                    removalOrder.gate_access_code = formik.values.gatecode;
                }
            } else {
                showError("Failed to update gate access code");
            }
        } catch (error: any) {
            const gateCodePayload = {
                version: removalOrder?.version,
                gate_access_code: formik.values.gatecode,
            };
            await handleApiError(error, {
                module: "removal",
                action: "Agent_View_Removal_Request_handle_save_gate_code",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                method: "PATCH",
                requestBody: gateCodePayload,
            }, token);
            showError(`Error updating gate access code: ${error.message}`);
        }
    };

    // Function to update instructions only
    const handleSaveInstructionsOnly = async (instructions: string) => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    version: removalOrder?.version,
                    additional_instructions: [{ value: instructions }],
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                showSuccess("Instructions updated successfully!");
                // Update the local state to reflect the change
                setInstructions(instructions);
                // Also update formik value to keep them in sync
                formik.setFieldValue("instructions", instructions);
            } else {
                showError("Failed to update instructions");
            }
        } catch (error: any) {
            showError(`Error updating instructions: ${error.message}`);
        }
    };

    // Initialize instructions from installation order after formik is declared
    useEffect(() => {
        if (removalOrder) {
            if (removalOrder?.additional_instructions) {
                const latestInstruction = removalOrder?.additional_instructions[removalOrder?.additional_instructions.length - 1];
                if (latestInstruction?.value) {
                    setInstructions(latestInstruction.value);
                    formik.setFieldValue("instructions", latestInstruction.value);
                }
            }
        }
    }, [removalOrder]);


    const removalLogEvents = React.useMemo(
        () => buildRemovalLogEventsFromOrder(removalOrder),
        [removalOrder]
    );

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            const id = removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${userId}/by-user/${id}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                safeConsole.log("PaymentMethod :", data);
                setPaymentMethod(Array.isArray(data) ? data : []);
            } catch (err) {
                await handleApiError(err, {
                    module: "removal",
                    action: "Agent_View_Removal_Request_fetch_payment_methods",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${userId}/by-user/${id}`,
                    method: "GET",
                }, token);
            } finally {
                setLoading(false);
            }
        };

        if (userId && removalOrder?.inventory_source) {
            fetchPaymentMethods();
        }
    }, [token, userId, removalOrder?.inventory_source]);

    const onPreviewInvoice = (t: InvoiceType) => {
        safeNavigate(`/agent/InvoiceDocument`, {
            state: {
                type: t,
                order: removalOrder, // pass the full order object used for the invoice
                imageUrlMap: undefined, // optional thumbs
                // logoUrl, serviceInfo, resolveImageSrc, ... if needed
                autoPrint: false, // set true if you want it to auto-open the print dialog
            },
        });
    };



    useEffect(() => {
        (async () => {
            if (!token || !removalOrder) return;

            const orderdata = (await GetVersionOrdernumber(
                token,
                removalOrder.order_number,
                removalOrder?.order_type || "removal"
            )) || null;
            const orderId = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";
            setOrderNumber(orderId);
        })();
    }, [token, removalOrder]);

    const recalcInstallationTaxes = React.useCallback(
        async (forceType?: "manual" | "taxcloud", options?: { allowRefund?: boolean }) => {
            try {
                await recompute();
            } catch (err) {
                safeConsole.error("[INSTALL:TAX] recompute before refund failed", err);
            }

            if (!options?.allowRefund) return false;
            if (!token || !removalOrder?.order_number) return false;
            if (removalOrder?.tax_status !== "complete") return false;
            if (forceType !== "taxcloud") return false;

            try {
                const orderdata = (await GetVersionOrdernumber(
                    token,
                    removalOrder.order_number,
                    removalOrder?.order_type || "removal"
                )) || null;
                const order_number = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";
                if (!order_number) throw new Error("Missing order number for refund");
                const success = await requestInstallationTaxRefund(token, order_number || "");
                if (!success) throw new Error("TaxCloud refund failed");
                return true;
            } catch (err) {
                safeConsole.error("[INSTALL:TAX] refund failed:", err);
                showError("Failed to request TaxCloud refund.");
                return false;
            }
        },
        [removalOrder?.order_number, removalOrder?.tax_status, recompute, token]
    );

    React.useEffect(() => {
        installationTaxRef.current = { recalculate: recalcInstallationTaxes };
    }, [recalcInstallationTaxes]);
    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content container">
            {

                <style>{`
                .custom-btn {
                      font-size: 14px;
                      font-weight: 600;
                  }
                  .custom-btn:hover {
                      background-color: #155dfc;
                      color: #fff;
                      font-size: 14px;
                      font-weight: 600;
                  }
        `}</style>
            }
            <div className="page-container ps-0">
                <h3 className="m-0 text-center fw-bold addheaders ps-1">
                    Removal Job #{removalOrder?.order_number} - {type === "View" ? "View" : "Edit"}
                </h3>
            </div>
            {/* Hidden invoice for PDF */}
            {/* <div style={{ position: "absolute", left: -99999, top: 0 }}>
                <div ref={invoiceRef}>
                    <OrderInvoice
                        order={normalizeRemovalToSalesOrder(removalOrder)}
                        customer={null as any}
                        leftAddressLabel="Bill to:"
                        rightAddressLabel="Removal to:"
                    />
                </div>
            </div> */}
            <div className="mb-4">
                <div className="align-items-center col-lg-12 d-flex d-lg-block flex-wrap justify-content-end justify-content-lg-between os-card">
                    {/* Removal Status Tracker Card */}
                    <div className="os-card" style={{
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        padding: '10px',
                        backgroundColor: 'white'
                    }}>
                        {/* Mobile/Tablet: Actions at top */}
                        {isViewMode && (
                            <div className="dropdown p-2 d-flex d-lg-none justify-content-end">
                                <button
                                    className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells custom-btn px-3"
                                    type="button"
                                    id="orderActionsDropdown"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Actions
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                    {removalOrder?.status === 100 && (
                                        <Link
                                            to={`/agent/jobs/viewRemovalRequest/${id}`}
                                            state={{ type: "Edit" }}
                                            className="dropdown-item text-primary table-cells"
                                        >
                                            <i className="ri-pencil-fill me-2"></i>Edit
                                        </Link>
                                    )}

                                    <Link to={""} className="dropdown-item text-primary table-cells">
                                        <i className="ri-mail-send-line me-2"></i>Email
                                    </Link>

                                    <li>
                                        <button className="dropdown-item" onClick={() => onPreviewInvoice("removal")}>
                                            <i className="bi bi-eye me-2" /> Preview Invoice
                                        </button>
                                    </li>

                                    {removalOrder?.status <= 300 && (
                                        <button
                                            className="dropdown-item text-danger table-cells"
                                            style={{ paddingLeft: 12 }}
                                            onClick={() => handleRemovalStatus(700)}
                                        >
                                            <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Job
                                        </button>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Desktop: Actions on right side */}
                        <div className="d-none d-lg-flex align-items-start gap-3">
                            {/* Left: Removal Status Tracker */}
                            <div className="flex-grow-1">
                                <InstallationStatusTracker
                                    type="removal"
                                    status={removalOrder?.status}
                                    paymentStatus={removalOrder?.payment_status}
                                    className="mb-0 w-100"
                                />
                            </div>

                            {/* Right: Actions Dropdown */}
                            {isViewMode && (
                                <div className="dropdown p-4">
                                    <button
                                        className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells custom-btn px-3"
                                        type="button"
                                        id="orderActionsDropdown"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Actions
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                        {removalOrder?.status === 100 && (
                                            <Link
                                                to={`/agent/jobs/viewRemovalRequest/${id}`}
                                                state={{ type: "Edit" }}
                                                className="dropdown-item text-primary table-cells"
                                            >
                                                <i className="ri-pencil-fill me-2"></i>Edit
                                            </Link>
                                        )}

                                        <Link to={""} className="dropdown-item text-primary table-cells">
                                            <i className="ri-mail-send-line me-2"></i>Email
                                        </Link>
                                        <li>
                                            <button className="dropdown-item" onClick={() => onPreviewInvoice("removal")}>
                                                <i className="bi bi-eye me-2" /> Preview Invoice
                                            </button>
                                        </li>

                                        {removalOrder?.status <= 300 && (
                                            <button
                                                className="dropdown-item text-danger table-cells"
                                                style={{ paddingLeft: 12 }}
                                                onClick={() => handleRemovalStatus(700)}
                                            >
                                                <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Job
                                            </button>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Mobile/Tablet: Removal Status Tracker */}
                        <div className="d-block d-lg-none">
                            <InstallationStatusTracker
                                type="removal"
                                status={removalOrder?.status}
                                paymentStatus={removalOrder?.payment_status}
                                className="mb-0 w-100"
                            />
                        </div>


                    </div>
                </div>
            </div>

            {/* <div className="page-container">
                <div className="d-flex justify-content-between align-items-center flex-wrap col-lg-12 os-card">
                    <div className="d-flex flex-grow-1 tracker-wrapper">
                        <InstallationStatusTracker type="removal" status={removalOrder?.status} paymentStatus={removalOrder?.payment_status} className="mb-3 w-75" />
                    </div>
                    {typeEditMode && (
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells"
                                type="button"
                                id="orderActionsDropdown"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                Actions
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                {
                                    removalOrder?.status == 100 && (
                                        <Link
                                            to={`/agent/jobs/viewRemovalRequest/${id}`} state={{ type: "Edit" }}
                                            className="dropdown-item text-primary table-cells"
                                        >
                                            <i className="ri-pencil-fill me-2"></i>Edit
                                        </Link>
                                    )
                                }
                                <Link to={""} className="dropdown-item text-primary table-cells">
                                    <i className="ri-mail-send-line me-2"></i>Email
                                </Link>

                                <Link to={""} className="dropdown-item text-primary table-cells">
                                    <i className="ri-file-download-line me-2"></i>Download Invoice
                                </Link>
                                {(removalOrder?.status <= 300) && (
                                    <button className="dropdown-item text-danger table-cells" onClick={() => handleRemovalStatus(700)}>
                                        <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Job
                                    </button>
                                )}

                            </ul>
                        </div>
                    )}
                </div>
            </div> */}

            <form onSubmit={formik.handleSubmit} className="me-2 me-lg-0">
                <div className="row col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0 p-3">
                    <div className="mb-3">
                        <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
                    </div>
                    <div className="col-lg-3 mb-3">
                        <label className="form-label table-cells">
                            Removal Job
                        </label>
                        <input type="text" name="order_number" id="order_number" className="form-control" placeholder="Enter Installation Job Order Number" value={`${removalOrder?.order_number || "—"}`} disabled />
                        {/* {formik.touched.order_number && formik.errors.order_number && (
                                    <div className="text-danger">{formik.errors.order_number}</div>
                                )} */}
                    </div>
                    <div className="col-lg-3 mb-3">
                        <label className="form-label table-cells">
                            Linked Installtion Job
                        </label>
                        <input
                            type="text"
                            id="installation_order_number"
                            name="installation_order_number"
                            className="form-control table-cells"
                            value={removalOrder?.installation_order_number || ''}
                            disabled
                        />
                    </div>
                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userEmail" className="form-label table-headers">Email <span className="text-danger">*</span></label>
                        <input
                            type="email"
                            className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail ? "is-invalid" : ""}`}
                            id="userEmail"
                            name="userEmail"
                            placeholder="Enter email"
                            value={formik.values.userEmail}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            readOnly={Viewmode || !isFieldEditable(removalOrder?.status ?? 0, "userEmail")}
                            disabled={Viewmode}
                        />
                        {(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail && (
                            <div className="invalid-feedback d-block">{formik.errors.userEmail as string}</div>
                        )}
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userPhone" className="form-label  table-headers">
                            Phone <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone ? "is-invalid" : ""}`}
                            id="userPhone"
                            name="userPhone"
                            placeholder="Enter phone"
                            value={formik.values.userPhone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            readOnly={Viewmode || !isFieldEditable(removalOrder?.status ?? 0, "userPhone")}
                            disabled={Viewmode}
                        />
                        {(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone && (
                            <div className="invalid-feedback d-block">{formik.errors.userPhone as string}</div>
                        )}
                    </div>
                </div>

                <div className="d-block d-lg-flex gap-3">
                    <div className="col-lg-8">
                        <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                            <div className="mb-3">
                                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                            </div>
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="scheduled_date"
                                    name="scheduled_date"
                                    min={new Date().toISOString().split("T")[0]}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.scheduled_date}
                                />
                                {formik.touched.scheduled_date && typeof formik.errors.scheduled_date === "string" && (
                                    <div className="invalid-feedback">{formik.errors.scheduled_date}</div>
                                )}
                            </div>
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Priority <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={priorityOptions}
                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                    onBlur={formik.handleBlur}
                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Priority")}
                                />
                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                )}
                            </div>
                        </div>
                        <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
                            <div className="align-items-center d-block d-lg-flex justify-content-between mb-3">
                                {/* Left side: Address Information with purple icon */}
                                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="mb-3 mb-lg-0" />

                                {/* Right side: Gate Code with functionality */}
                                <div className="d-flex align-items-center gap-3">
                                    <label className="table-cells mb-0">Gate Code</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="text"
                                            id="gatecode"
                                            name="gatecode"
                                            className="form-control"
                                            style={{
                                                width: '140px',
                                                backgroundColor: formik.values.gateCodeEnabled ? '#ffffff' : '#f8f9fa',
                                                color: formik.values.gateCodeEnabled ? '#495057' : '#6c757d',
                                                cursor: formik.values.gateCodeEnabled ? 'text' : 'pointer'
                                            }}
                                            value={formik.values.gatecode}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter Gate Code"
                                            readOnly={!formik.values.gateCodeEnabled}
                                            onClick={() => {
                                                if (!formik.values.gateCodeEnabled) {
                                                    formik.setFieldValue("gateCodeEnabled", true);
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-secondary table-addbutton btn-sm br-8"
                                            disabled={!formik.values.gateCodeEnabled}
                                            onClick={async () => {
                                                // Save gate code and disable the field again
                                                await handleSaveGateCode();
                                                formik.setFieldValue("gateCodeEnabled", false);
                                            }}
                                        >
                                            <i className="ri-save-fill fs-16"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-12">
                                <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                                    {/* <div className="my-2 fw-bold">
                                        <input
                                            type="checkbox"
                                            id="isSameAsBilling"
                                            name="isSameAsBilling"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            checked={formik.values.isSameAsBilling}
                                        />{" "}
                                        Billing and Installation Address are Same
                                    </div>
                                    <button
                                        className="btn btn-secondary mb-3 table-addbutton br-8"
                                        type="button"
                                        onClick={() => setShowAddressForm(true)}
                                        title="Add new address"
                                    >
                                        + Address
                                    </button> */}
                                </div>
                                <div className="row">
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="billingAddressId"
                                            className="form-label table-cells"
                                        >
                                            Billing Address <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            // className="form-control"
                                            id="billingAddress"
                                            name="billingAddress"
                                            options={addressOptions}
                                            value={
                                                addressOptions?.find(
                                                    (option) =>
                                                        option.value === formik.values.billingAddressId
                                                ) || null
                                            }
                                            onChange={(selected) => {
                                                safeConsole.log("Selected Billing Address:", selected); // Debugging log
                                                formik.setFieldValue("billingAddressId", selected?.value);
                                                const selectedAddress = addressObjects?.find((addr) => addr._id === selected?.value);
                                                setSelectedBillingAddress(selectedAddress);
                                                formik.setFieldValue("isSameAsBilling", false);
                                            }}
                                            isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "BillingAddress")}
                                            components={{ DropdownIndicator }}
                                            styles={customSelectStyles}
                                            classNamePrefix="rs"
                                        />
                                        {formik.touched.billingAddressId && typeof formik.errors.billingAddressId === "string" && (
                                            <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                        )}
                                    </div>
                                    {/* {formik.values.isSameAsBilling ? null : ( */}
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="removalAddressId"
                                            className="form-label table-cells mt-2"
                                        >
                                            Removal Address <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="removalAddressId"
                                            name="removalAddressId"
                                            value={`${removalOrder?.removal_details?.removal_address?.address_details?.addressLine1}, ${removalOrder?.removal_details?.removal_address?.address_details?.city}, ${removalOrder?.removal_details?.removal_address?.address_details?.state}`}
                                            disabled
                                        />
                                    </div>
                                    {/* )} */}
                                </div>
                            </div>
                            <div className="col-lg-12">
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker position={selectedLocation} />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-5 card-body shadow-lg mb-3 ms-0 p-3">
                            <div className="d-flex mb-3">
                                <span
                                    aria-hidden="true"
                                    className="image-icons"
                                    style={{
                                        backgroundColor: "#9810fa",
                                        WebkitMaskImage: `url(${ProductSelection})`,
                                        maskImage: `url(${ProductSelection})`,
                                    }}
                                />
                                <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                            </div>
                            {/* Desktop Table View */}
                            <div className="col-lg-12 ms-2 d-none d-lg-block" id="row-overlap">
                                <table className="table table-striped ">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center table-headers" style={{ width: "15%" }}>Product Name</th>
                                            <th className="text-center table-headers" style={{ width: "7%" }}>SKU</th>
                                            {
                                                isEdit &&
                                                <th className="text-center table-headers" style={{ width: "12%" }}>Installation Qty </th>
                                            }
                                            <th className="text-center table-headers" style={{ width: "10%" }}>Qty to Remove </th>
                                            {/* <th className="text-center table-headers" style={{ width: "15%" }}>Bin Location</th> */}
                                            <th className="text-center table-headers" style={{ width: "14%" }}>Product Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational")?.map((item: any, index: number) => (
                                            <tr key={index}>
                                                <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                <td className="text-center table-cells text-ellipsis">{displayRemovalSku(item)}</td>

                                                {
                                                    isEdit &&
                                                    <td className="text-center table-cells">
                                                        {(() => {
                                                            const orig = Number(item.quantity_to_be_removed) || 0;
                                                            const base = Number(item.available_quantity) || 0;
                                                            const pool = base + orig;
                                                            const planned = Number(removalQuantities[item.product_id] ?? (orig || 0));
                                                            return Math.max(0, pool - planned);
                                                        })()}
                                                    </td>
                                                }

                                                {/* Qty to remove (editable) */}
                                                <td className="text-center table-cells">
                                                    <input
                                                        type="number"
                                                        className="form-control text-center table-cells"
                                                        value={removalQuantities[item.product_id] ?? (item.quantity_to_be_removed || '')}
                                                        disabled={!isEdit}
                                                        min={0}
                                                        placeholder="0"
                                                        onChange={(e) => {
                                                            const rawValue = e.target.value;
                                                            // Allow empty string or convert to number
                                                            const qty = rawValue === '' ? '' : Number(rawValue);
                                                            const pool =
                                                                (Number(item.available_quantity) || 0) +
                                                                (Number(item.quantity_to_be_removed) || 0);
                                                            const clampedVal = rawValue === '' ? '' : Math.max(0, Math.min(Number(qty), pool));
                                                            setRemovalQuantities((prev) => ({
                                                                ...prev,
                                                                [item.product_id]: clampedVal as number,
                                                            }));
                                                        }}
                                                        onBlur={(e) => {
                                                            // On blur, if empty or 0, set to 1 as minimum
                                                            const currentValue = e.target.value;
                                                            if (currentValue === '' || Number(currentValue) <= 0) {
                                                                const pool = (Number(item.available_quantity) || 0) + (Number(item.quantity_to_be_removed) || 0);
                                                                setRemovalQuantities((prev) => ({
                                                                    ...prev,
                                                                    [item.product_id]: Math.min(1, pool),
                                                                }));
                                                            }
                                                        }}
                                                        onWheel={(e) => e.currentTarget.blur()}
                                                    />
                                                </td>

                                                {/* <td className="align-middle">
                                                    {(() => {
                                                        // When status is 600, prefer bin location from productNotes (final-submitted-items)
                                                        // Otherwise, use storage_locations
                                                        let binLocationDisplay = "";

                                                        if (removalOrder?.status === 600 && productNotes[item.product_id]?.bin_location_display) {
                                                            binLocationDisplay = productNotes[item.product_id].bin_location_display;
                                                        } else {
                                                            const productStorage = item?.storage_locations?.[0]?.location?.[0];
                                                            binLocationDisplay = productStorage?.warehouse_name
                                                                ? [
                                                                    productStorage.warehouse_name,
                                                                    productStorage.zone_name,
                                                                    productStorage.rack_name,
                                                                    productStorage.bin_name
                                                                ].filter(Boolean).join(' > ')
                                                                : "";
                                                        }

                                                        return (
                                                            <input
                                                                type="text"
                                                                className="form-control table-cells"
                                                                value={binLocationDisplay}
                                                                disabled={true}
                                                                title={binLocationDisplay}
                                                                data-path={`normal[${index}].binLocation`}
                                                            />
                                                        );
                                                    })()}
                                                </td> */}
                                                <td className="text-center table-cells align-middle">
                                                    {(() => {
                                                        // Check if restocked > 0 from finalSubmittedItems
                                                        if (removalOrder?.status === 600 && finalSubmittedItems) {
                                                            const productFinalItems = finalSubmittedItems.filter((fItem: any) => fItem.product_id === item.product_id);
                                                            const totalRestocked = productFinalItems.reduce((sum: number, fItem: any) => sum + (fItem.restocked || 0), 0);

                                                            if (totalRestocked > 0) {
                                                                // safeConsole.log(`Product ${item.product_id}: Has restocked ${totalRestocked}, showing with_supplier`);
                                                                return <StatusBadge status="with supplier" />;
                                                            }
                                                        }

                                                        // safeConsole.log("item.current_status in table view", item.current_status);
                                                        return <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />;
                                                    })()}
                                                </td>
                                            </tr>

                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile/Tablet Card View */}
                            <div className="col-lg-12 d-lg-none">
                                <div className="row g-3">
                                    {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational")?.map((item: any, index: number) => (
                                        <div key={index} className="col-12 mb-3">
                                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                <div className="card-body p-3">
                                                    {/* Product Name - Top Row */}
                                                    <div className="mb-3">
                                                        <h6>Product Name</h6>
                                                        <h6 className="card-title mb-0 fw-medium">
                                                            {item.product_name}
                                                        </h6>
                                                    </div>

                                                    {/* Quantity Inputs - Second Row */}
                                                    <div className="row g-2 mb-3">
                                                        {isEdit && (
                                                            <div className="col-6">
                                                                <label className="form-label small mb-1">Installation Qty</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-center"
                                                                    value={(() => {
                                                                        const orig = Number(item.quantity_to_be_removed) || 0;
                                                                        const base = Number(item.available_quantity) || 0;
                                                                        const pool = base + orig;
                                                                        const planned = Number(removalQuantities[item.product_id] ?? (orig || 0));
                                                                        return Math.max(0, pool - planned);
                                                                    })()}
                                                                    readOnly
                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                />
                                                            </div>
                                                        )}
                                                        <div className={isEdit ? "col-6" : "col-12"}>
                                                            <label className="form-label small mb-1">Qty to Remove</label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={removalQuantities[item.product_id] ?? (item.quantity_to_be_removed || '')}
                                                                disabled={!isEdit}
                                                                min="0"
                                                                placeholder="0"
                                                                max={(() => {
                                                                    const pool = (Number(item.available_quantity) || 0) + (Number(item.quantity_to_be_removed) || 0);
                                                                    return pool;
                                                                })()}
                                                                onChange={(e) => {
                                                                    const rawValue = e.target.value;
                                                                    // Allow empty string or convert to number
                                                                    const qty = rawValue === '' ? '' : Number(rawValue);
                                                                    const pool =
                                                                        (Number(item.available_quantity) || 0) +
                                                                        (Number(item.quantity_to_be_removed) || 0);
                                                                    const clampedVal = rawValue === '' ? '' : Math.max(0, Math.min(Number(qty), pool));
                                                                    setRemovalQuantities((prev) => ({
                                                                        ...prev,
                                                                        [item.product_id]: clampedVal as number,
                                                                    }));
                                                                }}
                                                                onBlur={(e) => {
                                                                    // On blur, if empty or 0, set to 1 as minimum
                                                                    const currentValue = e.target.value;
                                                                    if (currentValue === '' || Number(currentValue) <= 0) {
                                                                        const pool = (Number(item.available_quantity) || 0) + (Number(item.quantity_to_be_removed) || 0);
                                                                        setRemovalQuantities((prev) => ({
                                                                            ...prev,
                                                                            [item.product_id]: Math.min(1, pool),
                                                                        }));
                                                                    }
                                                                }}
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* SKU, Status, and Bin Location - Bottom Row */}
                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <h6>SKU</h6>
                                                            <span className="fw-medium text-ellipsis">{displayRemovalSku(item)}</span>
                                                        </div>
                                                        <div className="col-6">
                                                            <h6>Product Location</h6>
                                                            {(() => {
                                                                // Check if restocked > 0 from finalSubmittedItems
                                                                if (removalOrder?.status === 600 && finalSubmittedItems) {
                                                                    const productFinalItems = finalSubmittedItems.filter((fItem: any) => fItem.product_id === item.product_id);
                                                                    const totalRestocked = productFinalItems.reduce((sum: number, fItem: any) => sum + (fItem.restocked || 0), 0);

                                                                    if (totalRestocked > 0) {
                                                                        return <StatusBadge status="with supplier" />;
                                                                    }
                                                                }

                                                                return <StatusBadge status={item.current_status?.replace(/_/g, " ") || ""} />;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-3">
                            <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                                <Instructions
                                    title="Instructions"
                                    mode="instructions"
                                    loginType= {removalOrder?.status === 100 ? "signage" : "agent"}
                                    instructions={instructions}
                                    placeholder="Add instructions here"
                                    rows={4}
                                    onSave={handleSaveInstructionsOnly}
                                    className="mb-3"
                                    iconColor="#f54a00"
                                    iconBackground="#EAF7F1"
                                />
                            </div>
                            <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                                <div className="align-items-center d-flex mb-3">
                                    <span
                                        aria-hidden="true"
                                        className="image-icons"
                                        style={{
                                            backgroundColor: "#00a63e",
                                            WebkitMaskImage: `url(${Doller})`,
                                            maskImage: `url(${Doller})`,
                                        }}
                                    />
                                    <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                                </div>

                                {!Viewmode && (
                                    <OrderDiscountCodeField
                                        className="mb-2"
                                        value={couponInput}
                                        appliedCode={liveDiscountDisplay.code}
                                        discountAmount={liveDiscountDisplay.amount}
                                        disabled={isRecomputing}
                                        onChange={setCouponInput}
                                        onApply={(code: string) => {
                                            const trimmed = code.trim();
                                            if (!trimmed) return;
                                            userClearedCouponRef.current = false;
                                            setCouponInput(trimmed);
                                            formik.setFieldValue("discount_code", trimmed, false);
                                            void recompute({ discountCode: trimmed });
                                        }}
                                        onClear={() => {
                                            userClearedCouponRef.current = true;
                                            setCouponInput("");
                                            formik.setFieldValue("discount_code", "", false);
                                            formik.setFieldValue("discount_amount", 0, false);
                                            setZeroOrNegativeMsg("");
                                            void recompute({ discountCode: "" });
                                        }}
                                    />
                                )}
                                {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}

                                <hr />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Total Units:</span>
                                    <span className="fw-semibold">{Object.values(removalQuantities).reduce((sum, qty) => sum + qty, 0)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Removal Cost:</span>
                                    <span className="fw-semibold">${Number(removalCost || formik.values.installation_charge).toFixed(2)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Sub-total:</span>
                                    <span className="fw-semibold">
                                        ${Number(removalCost || formik.values.installation_charge).toFixed(2)}
                                    </span>
                                </div>
                                {Number(formik.values?.discount_amount || 0) > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="fw-medium" style={{ color: "#00a63e" }}>Discount:{formik.values?.discount_code}</span>
                                        <span className="fw-semibold text-success" style={{ color: "#00a63e" }}>
                                            -${Number(formik.values?.discount_amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    {(() => {
                                        const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                                        return (
                                            <>
                                                <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                                                    {labelText}:
                                                </span>
                                                <span className="fw-semibold flex-shrink-0">
                                                    ${Number(formik.values.totals.specialfee_subtotal || 0).toFixed(2)}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="py-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowTaxDetails(v => !v)}
                                            className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                            aria-expanded={showTaxDetails}
                                            aria-controls="tax-breakdown"
                                        >
                                            {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                                            <span className="order-card-title">Tax :</span>
                                        </button>
                                        <span className="order-card-cells">
                                            <span>
                                                ${(
                                                    Number(formik.values.totals.removal_tax_amount || 0) +
                                                    Number(formik.values.totals.specialfee_tax_amount || 0)
                                                ).toFixed(2)}
                                            </span>
                                        </span>
                                    </div>

                                    {/* {showTaxDetails && (
                                        <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>Removal Tax</span>
                                                <span className="order-card-cells">
                                                    ${Number(formik.values.totals.removal_tax_amount || 0).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span>Special Fee Tax</span>
                                                <span className="order-card-cells">
                                                    ${Number(formik.values.totals.specialfee_tax_amount || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )} */}
                                </div>

                                <hr className="my-3" />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-bold fs-5">Total Amount:</span>
                                    <span className="fw-bold fs-5 text-primary">
                                        {TotalAmount}
                                    </span>
                                </div>

                                <hr className="my-3" />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Already Paid:</span>
                                    <span className="fw-semibold">${totalPaid.toFixed(2)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Remaining Balance:</span>
                                    <span className="fw-semibold">
                                        {removalOrder?.payment_status === "Overpaid"
                                            ? ` +$${Math.abs(remaingamount).toFixed(2)}`
                                            : ` $${remaingamount.toFixed(2)}`
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Proof of Removal Section - Read Only for Agents */}
                        <div className="row col-lg-12 mb-3">
                            <div className="mb-3">
                                {(removalOrder?.status === 500 || removalOrder?.status === 600) && (
                                    <ProofSection
                                        title="Proof of Removal"
                                        canEdit={false}  // Read-only for agents
                                        existing={uploadedFileMeta}
                                        setExisting={setUploadedFileMeta}
                                        onSubmitProof={async (newFiles) => {
                                            // No-op for agents since canEdit is false
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* for desktop view back and submit buttom */}
                        <div className="row mb-3 col-lg-12 rcust-margin-4 d-none d-lg-block" style={{ marginTop: "27px" }}>
                            <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                <div className="col-lg-2 mb-3">
                                    <button
                                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                        type="button"
                                        onClick={() => safeNavigate(`/agent/jobs/removalRequests`)}
                                    >
                                        Back
                                    </button>
                                </div>

                                <div className="align-items-baseline col-lg-3 d-lg-flex justify-content-end">
                                    {/* {Viewmode && (removalOrder?.status !== 200) && (
                                        <>
                                            <button
                                                type="submit"
                                                className="btn btn-primary  px-4 table-cells actionbuttons"
                                                disabled={
                                                    formik.isSubmitting ||
                                                    (removalOrder?.status === 100 && (removalOrder?.payment_info?.length ?? 0) === 0)
                                                }

                                            >
                                                Pay & Confirm
                                            </button>
                                        </>
                                    )} */}
                                    {!Viewmode && (
                                        <button
                                            type="submit"
                                            className="btn btn-primary table-addbutton"
                                            disabled={formik.isSubmitting || isRecomputing}
                                        >
                                            {isRecomputing ? "Calculating..." : "Save"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* <div className="row col-lg-12 mb-3 rcust-margin-4">
                            <div className="col-lg-12 d-flex justify-content-between">
                                <div className="col-lg-3">
                                    <button
                                        className="btn btn-outline-secondary br-8 backbutton"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        Back
                                    </button>
                                </div>
                                   
                                <div className="col-lg-9 d-flex align-items-baseline justify-content-end">
                                    <div className="col-lg-4 mt-3">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                id="localLaws"
                                                name="localLawsChecked"
                                                className="form-check-input"
                                                checked={formik.values.localLawsChecked}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <label htmlFor="localLaws" className="form-check-label ms-2">
                                                I Acknowledge to local laws
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary  px-4 table-cells"
                                        disabled={formSubmitting || !formik.values.localLawsChecked}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div> */}
                    </div>
                    <div className="col-lg-4">
                        <PONumber
                            title="PO Number"
                            value={String((removalOrder as any)?.po_number ?? (removalOrder as any)?.poNumber ?? "")}
                            placeholder="e.g. PO-2026-00124"
                            className="mb-3"
                            orderId={String(removalOrder?._id || removalOrder?.id || id || "")}
                            orderType="removal"
                        />
                        {/* Show SpecialFeeui in edit mode always, or in view mode only if there are selected fees */}
                        {/* {(isEdit || (Viewmode && selectedSpecialFees.length > 0)) && ( */}
                        <SpecialFeeui
                            token={token || ""}
                            personId={removalOrder?.agent_id || removalOrder?.office?._id || userId || ""}
                            supplierId={removalOrder?.seller_owner_node_id || removalOrder?.signage_id || ""}
                            formik={formik}
                            name="specialfee"
                            value={selectedSpecialFees} // ✅ Pass state directly instead of relying on Formik
                            apiBase={process.env.REACT_APP_API_URL || ""}
                            readOnly={!isEdit}
                            disabled={isRecomputing}  // ✅ Disable special fee selection when recompute is running
                            title="Special Fees"
                            onOptionsLoaded={(allOptions) => {
                                // ✅ Receive all available fees (including required field) from component
                                setAllAvailableSpecialFees(allOptions);
                                safeConsole.log("All available special fees from component:", allOptions);
                            }}
                            onUserChange={(nextFees) => {
                                // ✅ Don't process if recompute is running (prevents duplicate calls)
                                if (isRecomputingRef.current) {
                                    safeConsole.log("⏸️ Recompute in progress, skipping special fee change");
                                    return;
                                }

                                safeConsole.log("SpecialFee changed - received from child:", nextFees);

                                // ✅ Only trigger recompute if fees actually changed (IDs or count)
                                const currentIds = selectedSpecialFees.map(f => f.id).sort().join(',');
                                const nextIds = nextFees.map(f => f.id).sort().join(',');

                                // ✅ Update state first (primary source)
                                setSelectedSpecialFees(nextFees);
                                // Also update Formik for form submission
                                formik.setFieldValue("specialfee", nextFees, false);

                                // Only recompute if selection changed (not just enrichment)
                                if (isEdit && currentIds !== nextIds) {
                                    safeConsole.log("🔄 Fee selection changed, triggering recompute");
                                    triggerRecomputeWith({ fees: nextFees });
                                }
                            }}
                        />
                        {/* )} */}

                        {
                            removalOrder?.payment_status === "Paid" ? <></> :
                                <div className="col-lg-12 mb-3">
                                    {removalOrder?.status <= 600 && Viewmode && (
                                        <PaymentPanel
                                            variant="agent"   // or brokerage/agent depending on type
                                            remainingAmount={Number(remaingamount)}
                                            orderStatus={removalOrder?.status ?? 0}
                                            paymentStatus={removalOrder?.payment_status ?? ""}
                                            selectedPaymentMethod={selectedPaymentMethod}
                                            setSelectedPaymentMethod={setSelectedPaymentMethod}
                                            formik={formik}
                                            maxAllowed={maxAllowed}
                                            paymentInfo={removalOrder?.payment_info ?? []}
                                            dynamicMethods={paymentMethod} // [{ _id, name }]
                                            onSubmitOffline={(key) => completeOrder(key)}
                                            orderType="removalOrder"
                                            onlinePaymentNode={
                                                stripePromise && clientSecret ? (
                                                    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                                                        <PaymentFormManualOrder
                                                            url="removal_orders"
                                                            method="POST"
                                                            supplierId={removalOrder?.signage_id}
                                                            totalAmount={remaingamount ? remaingamount : removalOrder?.total_amount}
                                                            paidAmount={
                                                                formik.values.partial_amount === 0
                                                                    ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                                    : formik.values.partial_amount
                                                            }
                                                            orderId={String(removalOrder?._id || removalOrder?.id || "")}
                                                            orderNumber={String(removalOrder?.order_number || "")}
                                                            customerName={String(removalOrder?.user_name || "")}
                                                            customerPhone={String(removalOrder?.user_phone || "")}
                                                            selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                                                            skipRedirectOnSuccess
                                                            onPaymentSuccess={async () => {
                                                                try {
                                                                    await finalizeTaxAfterPaidPayment({ force: true });
                                                                } catch (finalizeErr) {
                                                                    safeConsole.warn(
                                                                        "[TaxCloud] Post-payment finalize failed:",
                                                                        finalizeErr
                                                                    );
                                                                }
                                                                localStorage.removeItem("cart_id");
                                                                safeNavigate(0);
                                                            }}
                                                            userId={removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id}
                                                            userEmail={removalOrder?.user_email}
                                                            paidBy_id={userId?.toString() ?? ""}
                                                            paidBy_name={userName ?? ""}
                                                            payments_status="Paid"
                                                            onPaymentEmailCallback={sendRemovalPaymentAppliedEmails}
                                                        />
                                                    </Elements>
                                                ) : null
                                            }
                                            paymentMethodsNode={
                                                (() => {
                                                    const userIdForPayment = removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id;
                                                    return (
                                                        <PaymentMethods orderId={removalOrder?._id || ""} totalAmount={remaingamount ? remaingamount : removalOrder?.total_amount || 0} supplierId={removalOrder?.signage_id || ""} id={userIdForPayment || ""} ordertype="removalOrder" paymentInfo={removalOrder?.payment_info || []}
                                                        />
                                                    );
                                                })()
                                            }
                                        />
                                    )}
                                </div>
                        }

                        <div className="col-lg-12 rounded-3 shadow-sm">
                            <OrderLog title="Removal Log" orderKind="removal" events={removalLogEvents} />
                        </div>

                        {/* for mobile and tab view back and submit button */}
                        <div className="row mb-3 col-lg-12 rcust-margin-4 d-block d-lg-none" style={{ marginTop: '27px' }}>
                            <div className="col-12">
                                {/* Back Button - Full Width */}
                                <div className="col-12 mb-3">
                                    <button
                                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                        type="button"
                                        onClick={() => safeNavigate(`/agent/jobs/removalRequests`)}
                                    >
                                        Back
                                    </button>
                                </div>

                                {/* Submit Button - Full Width */}
                                <div className="col-12">
                                    {/* {Viewmode && (removalOrder?.status !== 200) && (
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 px-4 table-cells actionbuttons"
                                            disabled={
                                                formik.isSubmitting ||
                                                (removalOrder?.status === 100 && (removalOrder?.payment_info?.length ?? 0) === 0)
                                            }
                                        >
                                            Pay & Confirm
                                        </button>
                                    )} */}
                                    {!Viewmode && (
                                        <button
                                            type="submit"
                                            className="btn btn-primary table-addbutton"
                                            disabled={formik.isSubmitting || isRecomputing}
                                        >
                                            {isRecomputing ? "Calculating..." : "Save"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {showAddressForm && (
                    <AddressFormModal
                        isOpen={true}
                        onClose={() => {
                            setShowAddressForm(false);
                        }}
                        token={token || ""}
                        owner={{
                            value: userId || "",
                            label: userName || "",
                        }}
                        useType="billing"
                        prefill={{
                            label_of_address: formik?.values?.label_of_address || "",
                            firstName: formik?.values?.firstName || "",
                            lastName: formik?.values?.lastName || "",
                            company: formik?.values?.company || "",
                            phone: formik?.values?.phone || "",
                            addressLine1: formik?.values?.addressLine1 || "",
                            addressLine2: formik?.values?.addressLine2 || "",
                            city: formik?.values?.city || "",
                            state: formik?.values?.state || "",
                            zipcode: formik?.values?.zipcode || "",
                            country: formik?.values?.country || "",
                        }}
                        onCreated={(createdRaw, option, useType) => {
                            // Add the new address to the list
                            setAddressObjects((prev) => [createdRaw, ...(prev || [])]);

                            // Set the newly created address as selected
                            formik.setFieldValue(
                                useType === "billing" ? "billingAddressId" : "shippingAddressId",
                                option.value
                            );

                            if (useType === "billing") {
                                setSelectedBillingAddress(createdRaw);
                            } else {
                                setSelectedRemovalAddress(createdRaw);
                            }

                            // Mirror to shipping if same-as-billing is checked
                            if (useType === "billing" && formik.values.isSameAsBilling) {
                                formik.setFieldValue("shippingAddressId", option.value);
                                setSelectedRemovalAddress(createdRaw);
                            }
                        }}
                        afterCreateRefresh={() => {
                            // Refresh the addresses list
                            if (userId) {
                                const fetchAddresses = async () => {
                                    try {
                                        const response = await fetch(
                                            `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                                            {
                                                method: "GET",
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                    "Content-Type": "application/json",
                                                },
                                            }
                                        );
                                        const data = await response.json();
                                        setAddressObjects(data);
                                    } catch (err) {
                                        await handleApiError(err, {
                                            module: "removal",
                                            action: "Agent_View_Removal_Request_fetch_addresses_refresh",
                                            userId: userId || undefined,
                                            licenseType: licenseType || undefined,
                                            userName: userName || undefined,
                                            url: `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                                            method: "GET",
                                        }, token);
                                    }
                                };
                                fetchAddresses();
                            }
                        }}
                    />
                )}
            </form >

        </div >

    );
};

export default AgentViewRemovalRequest;
